// Agent fleet — the platform model-routing registry + the heavy lane's swarm.
//
// Routing table: every LLM CONSUMER in the platform and the model it runs on,
// grouped by WHERE it runs (in-product chat / the remote agent fleet / backend
// tasks) — the fleet section is where a vendor switch happens. Rows are named
// after what a user sees in the product (Board builds, chat, digests), not
// after internals; "Runs on" spells out the vendor with its billing model.
//
// Status closes the loop for fleet rows: the dispatcher's heartbeat carries
// the routes the pod actually applies (fleet_status.routes), so a row reads
// "applied by pod" / "pending" / "no live pod" / "drift" instead of a flat
// "active". Chat/backend consumers read the registry on next use → "active".
//
// The three chat personas share one default in practice; the table shows them
// as ONE row (writing to all three chat_* routes) until someone customizes
// per persona — the data model is unchanged, only the presentation folds.
//
// Access (like Cortex Audit): nav + route are mindshine-staff-gated (domain,
// any role); /api/fleet accepts any authenticated user (backend/modules/fleet).
//
// Two vendors since 2026-09-10: the Claude subscription (Claude Code CLI) and
// the ChatGPT subscription (Codex CLI) — a fleet row offers both where its
// allowed_vendors admits them (the orchestrator + subagent pair is Claude-only).
// A third, the GLM Coding Plan (Z.ai, run through the Claude Code CLI against
// Z.ai's Anthropic-compatible endpoint), since 2026-09-14/15: keys in the
// registry AND the pool, every fleet row but the subagent row may route to
// glm-5.3 / glm-5.3-flash, and the relay order is Claude → ChatGPT → GLM.
// Inside a GLM session the subagent aliases resolve to glm-5.3-flash (the
// worker reports that as the persona row's fallback). docs/fleet-vendor-glm-options.md.
// When every key of a row's vendor is used up the dispatcher RELAYS the job
// to the next vendor it can run (own vendor first, then any other), with a
// handoff document mid-job; the Status column then reads "on fallback" with
// the reason. Nothing is declared per row. docs/fleet-vendor-codex-options.md.
//
// MODELS & VENDORS AS A MATRIX (2026-09-15, docs/fleet-models-matrix-options.md
// §7, variant C — Kirill: "one model per cell, no lock, no frame"): the fleet
// rows are consumers × pooled vendors. EVERY cell is a model picker of that
// vendor's models = the model the row runs WHENEVER it runs on that vendor,
// natively or on relay (model_route.models, {vendor: model}); "not on this
// vendor" empties the cell and the relay skips the vendor (allowed_vendors is
// derived from the cells). WHERE the row runs now is the coloured switch under
// its name (the route, model_route.model_id = the lit vendor's cell) with the
// status chip under it. UNDER EVERY MODEL sits the KEY that vendor would use
// (heartbeat `<consumer>_relay` per vendor); the cell where the NEXT JOB of
// the row starts (heartbeat `<consumer>_key` — the pool's live pick, never the
// worker's hour-old fallback note) has a green frame and reads "● next job ·
// <key>", plus "+N key(s)" when the vendor has spare keys in the pool, plus a
// second grey line ONLY when the worker will run another model than the cell
// shows ("runs Opus · Fable week at 98%"). No fallback paragraphs, no relay
// order captions (Kirill, 2026-09-17: "I need to understand where the next
// task runs — on which key, under the model — and nothing after that"). The
// subagent row has no switch: it follows the orchestrator's. The chat rows
// keep the plain table above.
//
// SUBSCRIPTION KEYS = a TABLE (2a, Kirill 2026-09-16): one row per key, the
// key's own health as "State", a window cell = bar · % · → reset; no next-job
// badges (the matrix says which key a lane takes), no note column, no forecasts.
//
// "Fleet workers": the dispatcher's heartbeat rows (fleet_status) — alive /
// last seen, the routes the pod applies, and per-vendor subscription limit
// windows (the same numbers Claude Code's /usage shows), rendered generically
// from a vendor-neutral `windows` list so a vendor switch needs no UI change.
//
// "Working now — heavy lane" (components/fleet/HeavySwarm): the live swarm of
// heavy app-request jobs, polled every HEAVY_POLL_MS from GET /api/fleet/heavy
// (read from app_request + fleet_status — no fleet-side change). The registry
// itself loads once and on Reload.
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  getFleetModels, getFleetHeavy, setModelRoute, setModelRouteCell, getFleetSessions, getTranscriptUrl,
  getFleetKeys, addFleetKey, updateFleetKey, rotateFleetKey, removeFleetKey,
} from "../services/fleet";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useConfirm } from "@/components/ui/confirm-dialog";
import HeavySwarm from "../components/fleet/HeavySwarm";
import Hint from "../components/fleet/Hint";
import KeyChip, { KeyDot } from "../components/fleet/KeyChip";
import { fmtDur, fmtAgo, fmtClockAt, fmtQuota } from "../components/fleet/format";
import {
  Bot, Loader2, MessageSquare, Server, Zap, CircleCheck, CircleDashed, RefreshCw,
  Hammer, AlertTriangle, Radio, ChevronDown, ChevronRight, ListChecks, Plus, MoreHorizontal, KeyRound,
} from "lucide-react";

// Heavy-lane refresh cadence — the worker ticks every ~5s, so polling faster
// buys nothing; Audit/Docs poll their job lists at 4–8s.
const HEAVY_POLL_MS = 5000;

// Where a consumer runs. The `match` picks consumers by prefix so consumers
// added server-side land in the right section (or in "Other") unlisted here.
const SECTIONS = [
  { key: "chat", title: "In-product chat", note: "a new chat starts on this model; people can switch per chat", match: (c) => c.startsWith("chat_") },
  { key: "fleet", title: "Agent fleet (remote workers)", note: "applies to the next job within a minute; a running job keeps the model it started with", match: (c) => c.startsWith("fleet_") },
  { key: "backend", title: "Backend tasks", note: "read on next call", match: (c) => c.startsWith("backend_") },
  { key: "other", title: "Other", note: "", match: () => true },
];

// Display metadata per consumer — product words, not internals. Unknown
// consumers still render with a plain label so the page never hides a route.
// `exclude`: catalog ids this consumer must not be offered (fast analyst's
// SQL quality floor is Sonnet — the script documents it, the UI enforces it).
// `shape`: which claude_code row shape this consumer executes NATIVELY —
// "cli" = the alias rows `claude --model` takes (the fleet's CLI sessions),
// "api" = real Anthropic API ids (the chat spawn and the engine's LLM hop).
// One model, two spellings: offering both to one consumer reads as two models.
const CONSUMER_META = {
  chat_governance: { label: "Main chat (orchestrator)", icon: MessageSquare, hint: "Assistant panel on every page; delegates keep their built-in default", shape: "api" },
  chat_engine: { label: "Engine chat", icon: MessageSquare, hint: "Flow / engine surfaces", shape: "api" },
  chat_analyst: { label: "Analyst chat", icon: MessageSquare, hint: "Analyst panel in shared apps", shape: "api" },
  // `short` = the three-or-four-word line under the name in the matrix (the
  // full `hint` and the change history live on hover — Kirill, 2026-09-17).
  fleet_orchestrator: { label: "Orchestrator", icon: Bot, hint: "Every fleet job starts here — it plans and verifies, and delegates the work to subagents on the Builds & persona model", short: "plans, verifies, delegates", shape: "session", exclude: ["haiku"] },
  fleet_persona: { label: "Builds & persona jobs", icon: Hammer, hint: "Board builds, queued chat delegates, audits, workflow actors — the SUBAGENT model", shape: "cli", exclude: ["haiku"],
    note: "Subagents take a family's latest only — the Agent tool accepts the four aliases, not a version" },
  fleet_fast: { label: "Fast analyst", icon: Zap, hint: "Quick SQL answers · Sonnet or better", short: "quick SQL answers", shape: "session", exclude: ["haiku"] },
  fleet_requirements: { label: "Requirements generations", icon: ListChecks, hint: "Drafts, delta reviews, task plans, test specs — one pooled session per project · a JSON writing job", short: "Docs generations · JSON", shape: "session", exclude: ["haiku"] },
  fleet_review: { label: "Test reviewer", icon: ListChecks, hint: "Reads a red generated test's failure evidence and corrects it or says why it cannot be tested — one pooled session per project, one answer per batch · needs judgment", short: "fixes red generated tests", shape: "session", exclude: ["haiku"] },
  fleet_service: { label: "Backend one-shots", icon: Server, hint: "Feed digests · meeting summaries and action items · rule drafts · feature checklists — answered on the fleet's service lanes · a JSON writing job", short: "digests · summaries · drafts", shape: "session", exclude: ["haiku"] },
};

// Family order in the version picker (strongest first) and the human name.
const FAMILY_ORDER = ["fable", "opus", "sonnet", "haiku", "gpt"];
const FAMILY_LABEL = { fable: "Fable", opus: "Opus", sonnet: "Sonnet", haiku: "Haiku", gpt: "GPT", glm: "GLM" };
const familyOf = (m) => m.family || (/^(?:claude-)?(fable|opus|sonnet|haiku|gpt|glm)/.exec(m.id || "") || [])[1] || "";
// "2.1.251" ≥ "2.1.246"? — the CLI version gate of a pinned row (min_cli).
const cliAtLeast = (have, need) => {
  if (!have || !need) return true;
  const a = String(have).split(".").map(Number), b = String(need).split(".").map(Number);
  for (let i = 0; i < Math.max(a.length, b.length); i++) { const x = a[i] || 0, y = b[i] || 0; if (x !== y) return x > y; }
  return true;
};

// How the in-product chat is REALLY served on this stand (assistant /health via
// the registry response): the chat rows route at Anthropic API ids, but a pod
// without a subscription token serves them on Bedrock — dev and prod today. The
// "Runs on" cell says so instead of repeating the catalog vendor.
const CHAT_TRANSPORT = {
  bedrock: { label: "AWS Bedrock", billing: "API · pay per token", cls: "bg-amber-100 text-amber-800",
    note: "The assistant has no subscription token on this stand, so the chat runs this model on Bedrock. Fable is not offered here — it has no Bedrock profile." },
  "subscription-cli": { label: "Claude subscription", billing: "Claude Code CLI", cls: "bg-purple-100 text-purple-800" },
  "subscription-api": { label: "Claude subscription", billing: "Anthropic API · subscription token", cls: "bg-purple-100 text-purple-800" },
  gateway: { label: "Model gateway", billing: "LiteLLM proxy", cls: "bg-slate-100 text-slate-700" },
};
// Chat models that cannot be served on a given transport (no Bedrock profile).
const chatExcludeFor = (transport) => (transport === "bedrock" ? ["claude-fable-5", "claude-fable-5-1"] : []);

// The pickers offer the two subscriptions the fleet can run: Claude (Claude
// Code CLI) and, since 2026-09-10, ChatGPT (Codex CLI) — where a row's
// allowed_vendors admits it (the orchestrator and its subagents are Claude-only:
// the Agent tool spawns subagents inside the session's own CLI). Bedrock stays
// in the catalog and in the routes' allowed_vendors as a fallback an operator
// can route back to via the API, but it is not offered here: the pods run on
// the subscriptions since de-Bedrock (17e61628), and a picker choice nothing
// can apply is a trap, not an option. A stale route at another vendor still
// shows (the current value is always listed) so it can be moved off, not hidden.
const OFFERED_VENDORS = ["claude_code", "codex", "glm"]; // glm since 2026-09-15 (Claude Code CLI → Z.ai)
// The vendors whose keys the fleet POOLS today (keypool.RUNNABLE_VENDORS). A
// registry key of any other vendor is validated and kept, but no worker
// leases it yet — its block says so instead of promising it the next job.
const POOLED_VENDORS = ["claude_code", "codex", "glm"];
const CLI_ALIASES = ["fable", "opus", "sonnet", "haiku"];
const isCliAlias = (id) => CLI_ALIASES.includes(id);
const CHAT_CONSUMERS = ["chat_governance", "chat_engine", "chat_analyst"];

// Vendor → what a person needs to know: who runs it and how it is billed.
// A new vendor = one entry here (the backend keeps vendors as opaque ids).
const VENDOR_META = {
  bedrock: { label: "AWS Bedrock", billing: "API · pay per token", cls: "bg-amber-100 text-amber-800" },
  claude_code: { label: "Claude subscription", billing: "Claude Code CLI", cls: "bg-purple-100 text-purple-800" },
  codex: { label: "ChatGPT subscription", billing: "Codex CLI", cls: "bg-emerald-100 text-emerald-800" },
  glm: { label: "GLM Coding Plan", billing: "Claude Code CLI → Z.ai", cls: "bg-sky-100 text-sky-800" },
  openai: { label: "OpenAI API", billing: "pay per token", cls: "bg-emerald-100 text-emerald-800" },
};
const vendorMeta = (v) => VENDOR_META[v] || { label: v || "—", billing: "", cls: "bg-slate-100 text-slate-700" };

const fmt = (v) => {
  if (!v) return "";
  try { return new Date(v).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return String(v); }
};

// Relative age for heartbeat / snapshot timestamps (epoch seconds or ISO).
const ago = (v) => fmtAgo(v);
const fmtEpoch = (sec) => {
  if (!sec) return "";
  try { return new Date(sec * 1000).toLocaleString(undefined, { weekday: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
};
// Heartbeat is due every heartbeat_s (60s). A pod counts as alive for up to
// ALIVE_GRACE_MS without one: a backend roll drops a few beats in a row (the
// POST simply fails while the backend restarts), and flagging the fleet
// "silent" every deploy taught people to ignore the dot.
const ALIVE_GRACE_MS = 3 * 60 * 1000; // same rule as the backend's heavy lane: 3 heartbeats, 3-minute floor (was 10 min — the page and Working now disagreed on who is online)
const isAlive = (row) => {
  const t = new Date(row.seen_at).getTime();
  const period = (row.heartbeat_s || 60) * 1000;
  return Date.now() - t < Math.max(period * 3, ALIVE_GRACE_MS);
};

// What a CLI alias MEANS on the live workers. "Fable (always latest)" is not
// resolved by the API: the claude binary carries its own alias table, so the
// alias is exactly as new as the CLI baked into the worker image (2.1.246
// still ran `fable` as Fable 5 a day after Fable 5.1 shipped). Each pod's
// heartbeat reports that table (fleet_status.cli.resolves); this returns the
// distinct answers of the alive pods, most recent heartbeat first — two pods
// on different images can disagree, and that is exactly what to show.
// A resolved id the catalog does not carry yet (a model that shipped after the
// seed — `claude-fable-5-1` the week Fable 5.1 came out) still reads as a name,
// not as an id: "Fable 5.1". Display only; the catalog decides what is offered.
const prettyModelId = (id) => {
  const m = /^claude-(fable|opus|sonnet|haiku)-(\d+)(?:-(\d+))?/.exec(String(id || ""));
  if (!m) return id;
  const family = m[1][0].toUpperCase() + m[1].slice(1);
  return `${family} ${m[2]}${m[3] ? `.${m[3]}` : ""}`;
};

const aliasResolves = (alias, statusRows) => {
  const ids = statusRows.filter(isAlive).filter((p) => p.cli?.resolves?.[alias])
    .sort((a, b) => new Date(b.seen_at) - new Date(a.seen_at))
    .map((p) => p.cli.resolves[alias]);
  return [...new Set(ids)];
};

// A route edit the pod has not picked up within this long is drift, not lag
// (the dispatcher re-reads routes every 60s).
const PENDING_GRACE_MS = 5 * 60 * 1000;

// Fleet rows: compare the registry with what live pods report applying.
//   applied  — every alive pod runs this model
//   pending  — a pod is alive but still on the previous model (edit < 5 min)
//   drift    — alive pod(s) run something else for 5+ min → the pod cannot
//              run this model (missing vendor adapter / login) — the signal
//              to watch after a vendor switch
//   nopod    — no alive pod; the route is recorded, nobody applies it
function routeStatus(route, statusRows) {
  if (!route.wired) return { kind: "unwired" };
  if (!route.consumer.startsWith("fleet_")) return { kind: "active" };
  const alive = statusRows.filter(isAlive);
  if (!alive.length) return { kind: "nopod" };
  const reporting = alive.filter((p) => p.routes && p.routes[route.consumer] != null);
  if (!reporting.length) return { kind: "active" }; // older dispatcher: no routes in heartbeat
  const off = reporting.filter((p) => p.routes[route.consumer] !== route.model_id);
  if (!off.length) {
    const latest = reporting.map((p) => new Date(p.seen_at).getTime()).sort((a, b) => a - b).pop();
    return { kind: "applied", seen_at: new Date(latest).toISOString() };
  }
  // A pod that runs another model ON PURPOSE says why (the dispatcher's
  // fallback note rides along as `<consumer>_fallback_reason`): the Fable
  // week used up, every key of the row's vendor used up (the relay) — that
  // is a fallback in effect, not drift.
  const reason = off[0].routes[`${route.consumer}_fallback_reason`];
  if (reason) return { kind: "fallback", runs: off[0].routes[route.consumer], reason: String(reason) };
  const editedAgo = route.updated_at ? Date.now() - new Date(route.updated_at).getTime() : Infinity;
  return { kind: editedAgo < PENDING_GRACE_MS ? "pending" : "drift", runs: off[0].routes[route.consumer] };
}

function StatusCell({ route, statusRows, labelOf }) {
  const s = routeStatus(route, statusRows);
  const c = route.consumer;
  if (s.kind === "unwired") {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-page-muted" title="The consumer still uses its built-in default; this row takes effect when its phase is wired.">
        <CircleDashed size={13} /> not applied yet
      </span>
    );
  }
  // Two short lines: the verdict, then its detail — long single lines
  // overflowed the fixed-width column.
  const wrap = (cls, Icon, label, detail, title, extraTestId, spin) => (
    <span className="flex flex-col gap-0.5 text-xs items-start" data-testid={`fleet-wired-${c}`}>
      <Hint text={title} align="start">
        <span data-testid={extraTestId} className={`inline-flex items-center gap-1 whitespace-nowrap ${cls}`}>
          <Icon size={13} className={spin ? "animate-spin" : ""} /> {label}
        </span>
      </Hint>
      {detail && <span className="text-[11px] text-page-muted">{detail}</span>}
    </span>
  );
  switch (s.kind) {
    case "applied":
      return wrap("text-emerald-700", CircleCheck, "in use", `worker confirmed ${ago(s.seen_at)}`, "The worker reports running exactly this model. It re-reads the routing table about once a minute.", `fleet-applied-${c}`);
    case "pending":
      return wrap("text-amber-700", Loader2, "switching", "worker picks it up within a minute", `The worker still reports ${labelOf(s.runs)}. It re-reads the routing table about once a minute; this line turns green when it confirms the new model.`, `fleet-pending-${c}`, true);
    case "drift":
      return wrap("text-red-700", AlertTriangle, "not taken", `worker still runs ${labelOf(s.runs)} — check its adapter or login`, "The worker has not taken this model for over 5 minutes. Usually it cannot run this vendor at all: the adapter is missing or the account is not logged in on the worker.", `fleet-drift-${c}`);
    case "fallback":
      // The chip alone; the dispatcher's sentence lives in the hover — the
      // matrix cell says "runs Opus · Fable week at 98%" (Kirill, 2026-09-17).
      return wrap("text-amber-700", AlertTriangle, "on fallback", null, `${s.reason} — the worker is deliberately running this row on another model right now; the routed model comes back on its own when the reason clears (a window resets, a key answers again).`, `fleet-fallback-${c}`);
    case "nopod":
      return wrap("text-page-muted", Radio, "no worker online", "applies when one comes up", "No worker has reported a heartbeat recently. The choice is saved and takes effect as soon as a worker is online.", `fleet-nopod-${c}`);
    default:
      return wrap("text-emerald-700", CircleCheck, "in use", null, "Read from the routing table on the consumer's next call — there is nothing to wait for.");
  }
}

// One vendor's limit windows as bars. `utilization` is a 0..1 fraction (the
// subscription-quota vendors); RPM/TPM-style vendors fill remaining/limit.
function LimitWindows({ entry }) {
  const windows = Array.isArray(entry.windows) ? entry.windows : [];
  const vm = vendorMeta(entry.vendor);
  return (
    <div className="flex flex-col gap-1.5 min-w-[260px]" data-testid={`fleet-limits-${entry.vendor}`}>
      <div className="flex items-center gap-2 text-xs text-page-muted">
        <span className={`px-1.5 py-0 rounded-full text-[10px] font-medium ${vm.cls}`}>{vm.label}</span>
        <span>{entry.account || "subscription"}</span>
        <span>· {entry.source === "probe" ? "probe" : "from jobs"} {ago(entry.captured_at)}</span>
        {entry.status && entry.status !== "allowed" && (
          <span className="text-amber-700 font-medium">{entry.status}</span>
        )}
      </div>
      {windows.length === 0 && <div className="text-xs text-page-muted">n/a</div>}
      {windows.map((w) => {
        const pct = typeof w.utilization === "number" ? Math.round(w.utilization * 100) : null;
        const color = pct === null ? "#94a3b8" : pct >= 90 ? "#dc2626" : pct >= 70 ? "#d97706" : "#16a34a";
        return (
          <div key={w.key} className="flex items-center gap-2 text-xs" data-testid="fleet-limit-window">
            <span className="w-14 text-page-muted">{w.label || w.key}</span>
            <div className="flex-1 h-2 rounded-full bg-slate-200 overflow-hidden">
              {pct !== null && <div className="h-full rounded-full" style={{ width: `${Math.min(100, pct)}%`, background: color }} />}
            </div>
            <span className="w-10 text-right font-medium text-page-title tabular-nums">
              {pct !== null ? `${pct}%` : (w.remaining != null && w.limit != null ? `${w.remaining}/${w.limit}` : "—")}
            </span>
            <span className="w-[120px] text-page-muted">{w.resets_at ? `resets ${fmtEpoch(w.resets_at)}` : ""}</span>
          </div>
        );
      })}
    </div>
  );
}

const STATUS_WORDS = {
  allowed_warning: { text: "close to the limit", cls: "text-amber-700" },
  rejected: { text: "limit reached", cls: "text-red-700" },
  invalid: { text: "token invalid", cls: "text-red-700" },
};
const STATUS_HINTS = {
  rejected: "The provider is refusing requests: a window is used up. New jobs take another key while one has room; a job that hits this mid-run continues on another key in the same session. With no other key, jobs wait for the reset.",
  allowed_warning: "The provider marks this key as close to one of its limits. Requests still go through, but new jobs prefer another key while one has more room.",
  invalid: "The provider rejects this key's token itself (expired or revoked), so nothing runs on it. Re-issue it on the owning account with `claude setup-token` and update the fleet secret.",
};

// The short name of a subscription key: "Claude subscription (kirill)" → "kirill"
// (any vendor: the label sits in the trailing parentheses — dispatcher
// keypool.account_for). A bare vendor name (pre-pool history) has no key name.
const keyNameOf = (account) => {
  const a = String(account || "").trim();
  const m = a.match(/\(([^()]*)\)\s*$/);
  if (m) return m[1].trim();
  return Object.values(VENDOR_META).some((v) => v.label.toLowerCase() === a.toLowerCase()) ? "" : a;
};
// The account name a registry key reports under — the same template the
// dispatcher uses, so a key added on the page and its first heartbeat meet.
const accountOf = (vendor, label) => `${vendorMeta(vendor).label} (${label})`;
const SOURCE_HINTS = {
  registry: "This key lives in the platform key registry — the fleet's one place for keys. A change here reaches the fleet within a minute, no redeploy. Only its fingerprint is ever shown.",
  env: "EMERGENCY key: it comes from the pod's environment (a Kubernetes secret read at pod start), not from the registry. Normally the env carries no key at all. It cannot be changed from here, and a registry key with the same label is ignored while it is set — move it into the registry (Add key) and clear the env line.",
};

// The canonical windows per vendor, in display order. A key whose history
// lacks one still gets the cell (with an explanation) so the three columns
// line up across cards. Others reported by the provider are appended.
const CANON_WINDOWS = { claude_code: ["5h", "7d", "7d-fable"], codex: ["5h", "7d"], glm: ["5h", "7d"] };
const WINDOW_SHORT = { "5h": "5-hour window", "7d": "7-day window", "7d-fable": "7-day window · Fable", "7d-opus": "7-day · Opus", "7d-sonnet": "7-day · Sonnet" };
// A window older than the rest of the card by this much shows its own age.
const WINDOW_OWN_AGE_S = 3600;
const WINDOW_STALE_S = 6 * 3600;
const FABLE_WINDOW_HINT = "The provider reports the Fable week only while a job on this key runs the Fable model — the 10-minute probe (a haiku call) cannot see it. So this cell is as fresh as the last Fable job on this key, while 5-hour and 7-day refresh every 10 minutes.";

// Registry keys' enable switch (role=switch). Off = Disable (with the usual
// confirmation), on = Enable.
function KeySwitch({ on, onChange, label }) {
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={label} onClick={onChange} data-testid="fleet-key-switch"
      className={`relative inline-flex h-4 w-8 flex-none items-center rounded-full transition-colors cursor-pointer ${on ? "bg-emerald-500" : "bg-slate-300"}`}>
      <span className={`inline-block h-3 w-3 rounded-full bg-white shadow transition-transform ${on ? "translate-x-[18px]" : "translate-x-[2px]"}`} />
    </button>
  );
}

// The note, edited in place: click the text, type, Enter saves, Esc cancels.
function NoteEditor({ reg, canManage, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(reg.note || "");
  const who = [reg.added_by && `added by ${reg.added_by} ${ago(reg.added_at)}`,
    reg.updated_by && reg.updated_at !== reg.added_at && `changed by ${reg.updated_by} ${ago(reg.updated_at)}`].filter(Boolean).join(" · ");
  if (editing) {
    return (
      <input autoFocus value={val} maxLength={255} onChange={(e) => setVal(e.target.value)} data-testid="fleet-key-note-input"
        onKeyDown={(e) => { if (e.key === "Enter") { setEditing(false); if (val.trim() !== (reg.note || "")) onSave(val.trim()); } if (e.key === "Escape") { setVal(reg.note || ""); setEditing(false); } }}
        onBlur={() => { setEditing(false); if (val.trim() !== (reg.note || "")) onSave(val.trim()); }}
        placeholder="Whose subscription, why it is here" className="h-7 px-2 rounded-[5px] border border-card-border bg-page-bg text-page-title text-xs w-full max-w-[520px]" />
    );
  }
  return (
    <Hint align="start" text={`${who || "Registry key"}. ${canManage ? "Click to edit the note." : ""}`}>
      <span onClick={canManage ? () => setEditing(true) : undefined} data-testid="fleet-key-note-edit"
        className={`inline-flex items-center gap-1.5 text-xs ${reg.note ? "text-page-title" : "text-page-muted italic"} ${canManage ? "cursor-text" : ""}`}>
        <span>{reg.note || (canManage ? "add a note — whose subscription, why" : "")}</span>
        {canManage && <span className="text-page-muted border-b border-dashed border-slate-400 leading-none" aria-hidden="true">✎</span>}
      </span>
    </Hint>
  );
}

// One limit window as a cell: name, the percentage large, a bar, when it
// resets — and its OWN age when it is older than the rest of the card.
// ── Subscription keys as a TABLE (2a, Kirill 2026-09-16) ────────────────────
// One row per key: Key · Vendor · State · 5-hour window · 7-day window ·
// actions. "State" is the KEY'S OWN health only (usable · close to the limit
// · limit reached · invalid · disabled · waiting for the fleet · not in the
// pool · shadowed) — never the vendor's role: which key the next job takes and
// which vendor is the 1st / 2nd backup is the MATRIX's line, said once there.
// A window cell = bar · % · "→ <reset time>"; the Claude key's Fable week is a
// small line under its 7-day cell. No note column (the row menu edits it, the
// name's hover shows it), no forecasts, no credits.
const HEALTH = {
  usable: { text: "usable", cls: "bg-emerald-100 text-emerald-800", hint: "Every window has room; the pool may hand this key its vendor's next job." },
  warning: { text: "close to the limit", cls: "bg-amber-100 text-amber-800" },
  rejected: { text: "limit reached", cls: "bg-red-100 text-red-800" },
  invalid: { text: "invalid — rotate", cls: "bg-red-100 text-red-800" },
  disabled: { text: "disabled", cls: "bg-slate-100 text-slate-600", hint: "Switched off: the pool does not pick it for new jobs; a job already running on it finishes. Switch it on to put it back." },
  waiting: { text: "waiting for the fleet", cls: "bg-slate-100 text-slate-600", hint: "The registry has this key but no live worker has reported it yet. The dispatcher re-reads the registry every minute; the first job on the key produces its limit numbers." },
  unpooled: { text: "not in the pool yet", cls: "bg-sky-100 text-sky-800", hint: "The key is checked with the provider and kept in the registry, but the fleet's workers cannot run this vendor yet. Its quota is re-read by the platform's own key check every 15 minutes, so a dead key is still flagged here." },
  shadowed: { text: "shadowed by pod secret", cls: "bg-amber-100 text-amber-800", hint: "A pod-env key has the same label, and the pod secret wins: this registry row is not used. Remove the env entry from the fleet manifest (and redeploy) to switch the key to the registry, or give the registry key another label." },
};
const healthHint = (h) => HEALTH[h]?.hint || STATUS_HINTS[{ warning: "allowed_warning", rejected: "rejected", invalid: "invalid" }[h]] || "";

// One window of a key: bar · percent · "→ reset" (small = the Fable week line).
function WindowBar({ w, windowKey, cardUpdatedAt, small }) {
  const isFable = windowKey === "7d-fable";
  const name = WINDOW_SHORT[windowKey] || w?.label || windowKey;
  if (!w) {
    return (
      <div className={`text-page-muted ${small ? "text-[10.5px]" : "text-xs"}`} data-testid="fleet-limit-window" data-window={windowKey} data-missing="1">
        <Hint align="start" text={isFable ? FABLE_WINDOW_HINT : "The provider has not reported this window for this key yet."}>
          <span data-testid="fleet-window-missing">{isFable ? "no Fable job on this key yet" : "not reported yet"}</span>
        </Hint>
      </div>
    );
  }
  if (w.expired) {
    return (
      <div className={`text-page-muted ${small ? "text-[10.5px]" : "text-xs"}`} data-testid="fleet-limit-window" data-window={windowKey} data-expired="1">
        <Hint align="start" text={(isFable ? FABLE_WINDOW_HINT + " " : "") + `The last sample (${Math.round((w.utilization || 0) * 100)}% used) predates the window's reset at ${fmtClockAt(w.resets_at)}; no sample has been taken since, so today's figure is unknown.`}>
          <span data-testid="fleet-window-expired">no data since the reset {fmtClockAt(w.resets_at)}</span>
        </Hint>
      </div>
    );
  }
  const pct = Math.round((w.utilization || 0) * 100);
  const color = pct >= 90 ? "#dc2626" : pct >= 70 ? "#d97706" : "#16a34a";
  const ageS = cardUpdatedAt && w.captured_at ? cardUpdatedAt - w.captured_at : 0;
  const ownAge = ageS > WINDOW_OWN_AGE_S;
  if (small) {
    return (
      <div className="flex items-center gap-2 text-[10.5px] text-page-muted mt-1 whitespace-nowrap" data-testid="fleet-limit-window" data-window={windowKey}>
        <Hint align="start" text={FABLE_WINDOW_HINT}><span>Fable week</span></Hint>
        <span className="w-14 h-1.5 rounded-full bg-slate-200 overflow-hidden inline-block"><span className="block h-full rounded-full" style={{ width: `${Math.min(100, pct)}%`, background: color }} /></span>
        <span className="font-medium text-page-title tabular-nums">{pct}%</span>
        {ownAge && <span data-testid="fleet-window-age">· as of {ago(w.captured_at)}</span>}
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 whitespace-nowrap" data-testid="fleet-limit-window" data-window={windowKey}>
      <span className="w-[90px] h-2 rounded-full bg-slate-200 overflow-hidden inline-block shrink-0"><span className="block h-full rounded-full" style={{ width: `${Math.min(100, pct)}%`, background: color }} /></span>
      <span className="w-10 text-right font-semibold text-page-title tabular-nums text-sm">{pct}%</span>
      {w.resets_at ? (
        <Hint align="start" text={`This ${name} resets at ${fmtClockAt(w.resets_at)}, your local time${w.hours_to_reset != null ? ` — in ${fmtDur(w.hours_to_reset * 3600)}` : ""}; its usage drops back to 0 then. It is an allowance of the whole subscription — every worker on this account draws from it.`}>
          <span className="text-xs text-page-title">→ <span className="font-medium">{fmtClockAt(w.resets_at)}</span></span>
        </Hint>
      ) : <span className="text-xs text-page-muted">—</span>}
      {ownAge && <span className="text-[10.5px] text-page-muted" data-testid="fleet-window-age">· as of {ago(w.captured_at)}</span>}
    </div>
  );
}

function KeyRow({ row, canManage, showSource, onAction, noteOpen, onToggleNote }) {
  const { vendor, account, reg, ws, lv, health, disabled, name } = row;
  const vm = vendorMeta(vendor);
  const source = lv?.source || (reg ? "registry" : (lv ? "env" : null));
  const busy = Number.isFinite(Number(lv?.busy)) ? Number(lv.busy) : null;
  const updatedAt = Math.max(0, ...ws.map((w) => w.captured_at || 0), Number(lv?.captured_at) || 0);
  const byKey = new Map(ws.map((w) => [w.window_key, w]));
  const canon = CANON_WINDOWS[vendor] || ["5h", "7d"];
  const H = HEALTH[health] || HEALTH.usable;
  return (
    <>
      <tr className={`border-t border-card-border ${disabled ? "opacity-60" : ""}`} data-testid={`fleet-limits-${vendor}`} data-account={name} data-health={health}>
        <td className="px-4 py-2.5 align-top">
          <div className="flex items-center gap-1.5 font-medium text-page-title text-sm" title={reg?.note || undefined}>
            <span className="inline-flex items-center gap-1.5" data-testid="fleet-key-name">{name && <KeyDot label={name} />}<span className="font-mono">{name || "subscription"}</span></span>
          </div>
          {health === "invalid" && (
            <div className="text-[11px] text-red-700 mt-0.5" data-testid="fleet-key-invalid">
              {reg?.invalid_since ? `Token invalid since ${fmtClockAt(new Date(reg.invalid_since).getTime() / 1000)}. ` : ""}
              {(reg?.invalid_error || lv?.error) ? `Provider: ${reg?.invalid_error || lv.error}. ` : ""}No job can run on this key until its token is re-issued{reg ? " — Rotate token in the row menu" : " and the fleet secret updated"}.
            </div>
          )}
          <div className="flex items-center flex-wrap gap-x-1.5 text-[11px] text-page-muted mt-0.5">
            {(reg?.fingerprint || lv?.fingerprint) && (
              <Hint text="The first 8 hex characters of the token's SHA-256 — enough to tell two keys apart without ever showing either.">
                <span className="font-mono" data-testid="fleet-key-fingerprint">#{reg?.fingerprint || lv?.fingerprint}</span>
              </Hint>
            )}
            {busy !== null && <span data-testid="fleet-key-busy">· {busy === 0 ? "idle" : `${busy} job${busy === 1 ? "" : "s"} running on it`}</span>}
            {updatedAt > 0 && <span>· updated {ago(updatedAt)}</span>}
            {disabled && <span data-testid="fleet-key-disabled">· disabled{reg.disabled_at ? ` ${ago(reg.disabled_at)}` : ""}{reg.updated_by ? ` by ${reg.updated_by.split("@")[0]}` : ""}</span>}
            {showSource && source && (
              <Hint text={SOURCE_HINTS[source]}>
                <span className={`px-1.5 py-0 rounded-full text-[10px] font-medium ${source === "registry" ? "bg-sky-100 text-sky-800" : "bg-amber-100 text-amber-800"}`} data-testid="fleet-key-source">
                  {source === "registry" ? "registry" : "emergency env key"}
                </span>
              </Hint>
            )}
          </div>
        </td>
        <td className="px-3 py-2.5 align-top whitespace-nowrap"><span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${vm.cls}`}>{vm.label}</span></td>
        <td className="px-3 py-2.5 align-top whitespace-nowrap">
          <Hint align="start" text={healthHint(health)}>
            <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap ${H.cls}`} data-testid={health === "unpooled" ? "fleet-key-unpooled" : "fleet-quota-status"}>{H.text}</span>
          </Hint>
        </td>
        <td className="px-3 py-2.5 align-top"><WindowBar w={byKey.get(canon[0] || "5h")} windowKey={canon[0] || "5h"} cardUpdatedAt={updatedAt} /></td>
        <td className="px-3 py-2.5 align-top">
          <WindowBar w={byKey.get(canon[1] || "7d")} windowKey={canon[1] || "7d"} cardUpdatedAt={updatedAt} />
          {canon.includes("7d-fable") && <WindowBar w={byKey.get("7d-fable")} windowKey="7d-fable" cardUpdatedAt={updatedAt} small />}
        </td>
        <td className="px-3 py-2.5 align-top text-right whitespace-nowrap">
          {reg && canManage && (
            <span className="inline-flex items-center gap-2">
              <Hint text={disabled ? "Enable: the pool picks this key again within a minute." : "Disable: stop placing new jobs on this key; a job already running on it finishes."}>
                <KeySwitch on={!disabled} label={disabled ? `Enable key ${name}` : `Disable key ${name}`} onChange={() => onAction(disabled ? "enable" : "disable", reg)} />
              </Hint>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button type="button" className="inline-flex items-center justify-center h-6 w-6 rounded-[5px] text-page-muted hover:text-page-title hover:bg-slate-100 cursor-pointer" data-testid="fleet-key-menu" title="Key actions">
                    <MoreHorizontal size={14} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={() => onAction("rotate", reg)} data-testid="fleet-key-rotate">Rotate token…</DropdownMenuItem>
                  <DropdownMenuItem onSelect={onToggleNote} data-testid="fleet-key-note-toggle">{noteOpen ? "Hide the note" : (reg.note ? "Show / edit the note…" : "Add a note…")}</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="text-red-700" onSelect={() => onAction("remove", reg)} data-testid="fleet-key-remove">Remove from the registry</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </span>
          )}
        </td>
      </tr>
      {noteOpen && reg && (
        <tr className="bg-page-bg" data-testid="fleet-key-note-row" data-account={name}>
          <td colSpan={6} className="px-4 py-2" data-testid="fleet-key-note">
            <NoteEditor reg={reg} canManage={canManage} onSave={(note) => onAction("setnote", reg, note)} />
          </td>
        </tr>
      )}
    </>
  );
}

// Add a key / rotate a key's token. The secret goes up once and is never
// shown again; the backend validates it with the provider before storing
// (a refused token is reported with the provider's own message).
function KeyDialog({ state, vendors, onClose, onDone }) {
  const rotate = state?.mode === "rotate";
  const [label, setLabel] = useState(state?.label || "");
  const [vendor, setVendor] = useState(state?.vendor || vendors[0]?.id || "claude_code");
  const [secret, setSecret] = useState("");
  const [note, setNote] = useState(state?.note || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const vm = vendors.find((v) => v.id === vendor) || vendors[0];
  const submit = async (e) => {
    e?.preventDefault?.();
    setBusy(true); setError(null);
    try {
      const res = rotate
        ? await rotateFleetKey(state.label, secret.trim())
        : await addFleetKey({ label: label.trim().toLowerCase(), vendor, secret: secret.trim(), note: note.trim() });
      const ws = (res?.probe?.windows || []).map((w) => `${w.label || w.key} ${Math.round((w.utilization || 0) * 100)}%`).join(" · ");
      toast.success(rotate ? `Token rotated for “${state.label}”` : `Key “${res.key.label}” added`, { description: ws ? `Provider accepted it. Usage now: ${ws}. The fleet picks it up within a minute.` : "The fleet picks it up within a minute." });
      onDone();
    } catch (err) {
      setError(err?.message || "Request failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={!!state} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-[480px]" data-testid="fleet-key-dialog">
        <DialogHeader>
          <DialogTitle>{rotate ? `Rotate the ${vm?.cred_kind === "auth_file" ? "login file" : "token"} of “${state.label}”` : "Add a subscription key"}</DialogTitle>
          <DialogDescription>
            {rotate
              ? "Paste the new token. The key keeps its label and its history; the fleet switches to the new token within a minute."
              : "The token is checked with the provider, stored in the platform database, and reaches the fleet within a minute — no redeploy. It is never shown on this page again; the page keeps a fingerprint."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="flex flex-col gap-3 text-sm">
          {!rotate && (
            <>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-page-muted">Label — the key's name on this page (lowercase letters, digits, dashes)</span>
                <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. kirill, team-b" required pattern="[a-z0-9][a-z0-9\-]{0,31}" autoComplete="off" spellCheck={false}
                  className="h-8 px-2 rounded-[5px] border border-card-border bg-page-bg text-page-title" data-testid="fleet-key-dialog-label" />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-page-muted">Vendor</span>
                <select value={vendor} onChange={(e) => setVendor(e.target.value)} className="h-8 px-2 rounded-[5px] border border-card-border bg-page-bg text-page-title" data-testid="fleet-key-dialog-vendor">
                  {vendors.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
                </select>
              </label>
            </>
          )}
          <label className="flex flex-col gap-1">
            <span className="text-xs text-page-muted">{vm?.hint || "Token"}</span>
            {vm?.cred_kind === "auth_file" ? (
              // A credential that is a FILE (Codex: ~/.codex/auth.json) — pasted
              // whole; the backend parses and stores it compact. Not masked:
              // the paste is what the person is looking at anyway, and a
              // masked 4 KB blob cannot be checked for a truncated paste.
              <textarea value={secret} onChange={(e) => setSecret(e.target.value)} required minLength={20} rows={6} spellCheck={false}
                placeholder={'{ "auth_mode": "chatgpt", "tokens": { "id_token": "…", "access_token": "…", "refresh_token": "…", "account_id": "…" }, "last_refresh": "…" }'}
                className="px-2 py-1.5 rounded-[5px] border border-card-border bg-page-bg text-page-title font-mono text-[11px] leading-snug resize-y" data-testid="fleet-key-dialog-secret" />
            ) : (
              <input type="password" value={secret} onChange={(e) => setSecret(e.target.value)} required minLength={20} autoComplete="new-password" spellCheck={false} placeholder={vm?.cred_kind === "api_key" ? "1a2b3c…0d1e.AbCdEfGhIjKlMnOp" : "sk-ant-oat01-…"}
                className="h-8 px-2 rounded-[5px] border border-card-border bg-page-bg text-page-title font-mono" data-testid="fleet-key-dialog-secret" />
            )}
          </label>
          {!rotate && (
            <label className="flex flex-col gap-1">
              <span className="text-xs text-page-muted">Note (optional) — whose subscription, why</span>
              <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={255} placeholder="e.g. Kirill's personal Max plan" className="h-8 px-2 rounded-[5px] border border-card-border bg-page-bg text-page-title" data-testid="fleet-key-dialog-note" />
            </label>
          )}
          {error && <div className="text-xs text-red-700" data-testid="fleet-key-dialog-error">{error}</div>}
          <div className="flex justify-end gap-2 mt-1">
            <button type="button" onClick={onClose} className="h-8 px-3 rounded-[5px] text-sm text-page-muted hover:text-page-title border border-card-border bg-transparent cursor-pointer">Cancel</button>
            <button type="submit" disabled={busy} className="inline-flex items-center gap-1.5 h-8 px-3 rounded-[5px] text-sm text-white bg-[#5b4fd6] hover:opacity-90 disabled:opacity-60 cursor-pointer" data-testid="fleet-key-dialog-submit">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />} {busy ? "Checking with the provider…" : (rotate ? "Rotate" : "Add key")}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Model picker grouped by vendor. Offers enabled models the consumer can
// EXECUTE natively: offered vendors only (Claude subscription for now — see
// OFFERED_VENDORS), the route's vendor allow-list, the consumer's row shape
// (CLI aliases for fleet sessions, API ids for chat/backend hops), the chat
// flag for chat consumers, and per-consumer excludes. The current model always
// stays listed so a legacy incompatible route is visible — and flagged — not
// hidden.
// `shape`: "cli" = aliases only (the subagent row), "api" = Anthropic API ids
// only (chat), "session" = a CLI session's `--model`: the alias ("always
// latest") AND the pinned versions of the family — grouped by family, the
// alias first with what it resolves to on the live worker ("Fable (always
// latest → 5.1)"), then the versions newest first. `resolveAlias(id)` → the
// resolved model's label, or "" when no worker reports it.
function ModelSelect({ value, catalog, allowed, chatOnly, shape, exclude, onChange, disabled, testid, resolveAlias, allowNone, noneLabel, className }) {
  const shapeOk = (m) => !shape
    || (shape === "cli" ? isCliAlias(m.id) : shape === "api" ? !isCliAlias(m.id) : (isCliAlias(m.id) || m.pinned === true));
  // An excluded ALIAS excludes its whole family: "no haiku" on a session row
  // means no Haiku 4.5 either, not just the floating spelling.
  const excludedFamilies = (exclude || []).filter(isCliAlias);
  const excluded = (m) => (exclude || []).includes(m.id) || (excludedFamilies.length > 0 && excludedFamilies.includes(familyOf(m)));
  const offered = catalog.filter((m) =>
    (value && m.id === value) || (m.enabled && OFFERED_VENDORS.includes(m.vendor) && (!allowed.length || allowed.includes(m.vendor)) && shapeOk(m) && (!chatOnly || m.chat !== false) && !excluded(m)));
  const labelFor = (m) => {
    if (shape === "session" && isCliAlias(m.id)) {
      const res = resolveAlias ? resolveAlias(m.id) : "";
      return res ? `${FAMILY_LABEL[familyOf(m)] || m.label} (always latest → ${res})` : m.label;
    }
    return m.label;
  };
  const groups = [];
  if (shape === "session") {
    const verNum = (v) => String(v || "0").split(".").map(Number);
    const cmpVer = (a, b) => { const x = verNum(a), y = verNum(b); for (let i = 0; i < Math.max(x.length, y.length); i++) { const d = (y[i] || 0) - (x[i] || 0); if (d) return d; } return 0; };
    for (const fam of [...FAMILY_ORDER, ...[...new Set(offered.map(familyOf))].filter((f) => !FAMILY_ORDER.includes(f))]) {
      const items = offered.filter((m) => familyOf(m) === fam)
        .sort((a, b) => (isCliAlias(b.id) - isCliAlias(a.id)) || cmpVer(a.version, b.version));
      if (items.length) groups.push({ vendor: items[0].vendor, key: fam, label: `${FAMILY_LABEL[fam] || fam} · ${vendorMeta(items[0].vendor).label}`, items });
    }
  } else {
    for (const m of offered) {
      let g = groups.find((x) => x.vendor === m.vendor);
      if (!g) { g = { vendor: m.vendor, key: m.vendor, label: `${vendorMeta(m.vendor).label} · ${vendorMeta(m.vendor).billing}`, items: [] }; groups.push(g); }
      g.items.push(m);
    }
  }
  return (
    <select
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      data-testid={testid}
      className={className || "h-8 px-2 rounded-[5px] border border-card-border bg-page-bg text-page-title text-sm w-full max-w-[300px]"}
    >
      {allowNone && <option value="">{noneLabel || "— none —"}</option>}
      {groups.map((g) => (
        <optgroup key={g.key} label={g.label}>
          {g.items.map((m) => <option key={m.id} value={m.id}>{labelFor(m)}</option>)}
        </optgroup>
      ))}
    </select>
  );
}

// ── Sessions: every worker session the fleet ran, with its transcripts ──────
// One row per session (fleet_session): what it was about, the cards it worked,
// the model, how it ended, and the FULL Claude Code transcripts stored on S3
// forever (session_transcript) — downloadable from here.
function fmtBytes(n) {
  n = Number(n) || 0;
  return n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;
}
function FleetSessions() {
  const PAGE = 20;
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [projects, setProjects] = useState([]);
  const [project, setProject] = useState("");
  const [keyFilter, setKeyFilter] = useState("");
  const [keyCounts, setKeyCounts] = useState([]);
  const [limit, setLimit] = useState(PAGE);
  const [err, setErr] = useState(null);
  const [open, setOpen] = useState({});
  const load = async (lim, proj, key = keyFilter) => {
    try {
      const res = await getFleetSessions({ limit: lim, project: proj || undefined, key: key || undefined });
      setRows(res.sessions || []); setTotal(res.total ?? (res.sessions || []).length); if (Array.isArray(res.projects)) setProjects(res.projects);
      if (Array.isArray(res.keys)) setKeyCounts(res.keys);
      setErr(null);
    } catch (e) { setErr(e?.message || "Failed to load the fleet sessions"); }
  };
  useEffect(() => {
    load(limit, project, keyFilter);
    const t = setInterval(() => { if (document.visibilityState === "visible") load(limit, project, keyFilter); }, 30_000);
    return () => clearInterval(t);
  }, [limit, project, keyFilter]);
  const download = async (tid) => {
    try {
      const res = await getTranscriptUrl(tid);
      if (res?.url) window.open(res.url, "_blank", "noopener");
    } catch (e) { toast.error(e?.message || "could not fetch the transcript"); }
  };
  const shown = (rows || []).length;
  return (
    <div className="mt-6" data-testid="fleet-sessions">
      <div className="flex items-center flex-wrap gap-2 mb-1">
        <h2 className="text-sm font-medium text-page-title">Sessions</h2>
        <span className="text-xs text-page-muted" data-testid="fleet-sessions-count">· last {Math.min(shown, total)} of {total}{project ? ` in ${project}` : ""}</span>
        <select value={project} onChange={(e) => { setProject(e.target.value); setLimit(PAGE); }} data-testid="fleet-sessions-project"
          className="h-7 px-2 rounded-[5px] border border-card-border bg-page-bg text-page-title text-xs">
          <option value="">all projects</option>
          {projects.map((p) => <option key={p.project_slug} value={p.project_slug}>{p.project_slug} ({p.n})</option>)}
        </select>
        {keyCounts.length > 0 && (
          <span className="inline-flex items-center gap-1 text-xs" data-testid="fleet-sessions-keys">
            <button type="button" onClick={() => { setKeyFilter(""); setLimit(PAGE); }} className={`h-6 px-2 rounded-full border text-[11px] cursor-pointer ${keyFilter === "" ? "border-[#5b4fd6] bg-[#ECEAFB] text-[#3F35B5]" : "border-card-border bg-card-bg text-page-muted"}`}>all keys</button>
            {keyCounts.map((k) => (
              <button key={k.key} type="button" onClick={() => { setKeyFilter(k.key); setLimit(PAGE); }} data-testid={`fleet-sessions-key-${k.key}`}
                className={`inline-flex items-center gap-1 h-6 px-2 rounded-full border text-[11px] cursor-pointer ${keyFilter === k.key ? "border-[#5b4fd6] bg-[#ECEAFB] text-[#3F35B5]" : "border-card-border bg-card-bg text-page-muted"}`}>
                <KeyDot label={k.key} size={7} />{k.key} · {k.n}
              </button>
            ))}
          </span>
        )}
      </div>
      <div className="text-xs text-page-muted mb-2">
        Every worker session the fleet ran — what it was about, the cards it worked, the model, how it ended. A session with no sign of life for six hours is marked <b>lost</b>.
        Full Claude Code transcripts are kept on S3 forever; download them here.
      </div>
      {err && <div className="text-sm text-red-500 mb-2">{err}</div>}
      {rows && rows.length === 0 && <div className="text-xs text-page-muted">No sessions recorded yet.</div>}
      {(rows || []).map((s) => {
        const sid8 = String(s.session_id).slice(0, 8);
        const done = (s.cards || []).filter((c) => c.status === "done").length;
        const isOpen = !!open[s.session_id];
        const outcomeCls = !s.outcome ? "bg-sky-500/10 text-sky-700 dark:text-sky-400"
          : (s.outcome === "done" || s.outcome === "drained") ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
          : s.outcome === "lost" ? "bg-slate-500/10 text-slate-600 dark:text-slate-300"
          : "bg-amber-500/10 text-amber-700 dark:text-amber-400";
        return (
          <div key={s.session_id} className="border border-card-border rounded-[8px] bg-card-bg px-4 py-3 mb-2" data-testid={`fleet-session-${sid8}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-xs text-page-muted" title={s.session_id}>{sid8}</span>
              {s.project_slug && <span className="text-xs px-1.5 py-0.5 rounded bg-card-border/40 text-page-muted">{s.project_slug}</span>}
              {s.kind && <span className="text-xs px-1.5 py-0.5 rounded bg-card-border/40 text-page-muted">{s.kind}</span>}
              {s.model && <span className="text-xs px-1.5 py-0.5 rounded bg-sky-500/10 text-sky-700 dark:text-sky-400">{s.model}</span>}
              {s.key && <KeyChip label={s.key} title={`ran on key ${s.key}`} testid={`fleet-session-key-${sid8}`} />}
              <span className={"text-xs px-1.5 py-0.5 rounded " + outcomeCls} data-testid={`fleet-session-outcome-${sid8}`}>
                {s.outcome || "running"}{s.rounds ? ` · ${s.rounds} round${s.rounds === 1 ? "" : "s"}` : ""}
              </span>
              <span className="text-xs text-page-muted ml-auto">
                started {new Date(s.started_at).toLocaleString()}{s.ended_at ? ` · ${fmtDur(Math.max(0, (new Date(s.ended_at) - new Date(s.started_at)) / 1000))}` : ""}
              </span>
            </div>
            <div className="text-sm text-page-title mt-1" data-testid={`fleet-session-summary-${sid8}`}>{s.summary || "—"}</div>
            <div className="flex flex-wrap items-center gap-3 mt-1.5 text-xs text-page-muted">
              {(s.cards || []).length > 0 && (
                <button type="button" onClick={() => setOpen((o) => ({ ...o, [s.session_id]: !isOpen }))}
                  className="bg-transparent border-0 p-0 cursor-pointer text-nav-active hover:underline">
                  {(s.cards || []).length} card{(s.cards || []).length === 1 ? "" : "s"} · {done} done {isOpen ? "▲" : "▼"}
                </button>
              )}
              {(s.transcripts || []).map((t) => (
                <button key={t.id} type="button" data-testid={`fleet-transcript-${t.id}`} onClick={() => download(t.id)}
                  title={`Download ${t.key}`}
                  className="bg-transparent border-0 p-0 cursor-pointer text-nav-active hover:underline inline-flex items-center gap-1">
                  ⬇ {t.name.replace(`${s.session_id}`, "session")} ({fmtBytes(t.bytes)}{t.lines ? `, ${t.lines} lines` : ""})
                </button>
              ))}
              {(s.transcripts || []).length === 0 && <span>no transcript stored yet</span>}
              {(s.pod || s.worker) && <span className="ml-auto font-mono text-[11px]" title={`${s.worker || ""}${s.pod ? ` @ ${s.pod}` : ""}`}>{s.pod ? `pod ${String(s.pod).replace(/^cortex-agents-/, "")}` : s.worker}</span>}
            </div>
            {isOpen && (
              <ul className="mt-2 text-xs text-page-muted list-disc pl-5 max-h-64 overflow-auto">
                {(s.cards || []).map((c) => (
                  <li key={c.id}><span className="font-mono">#{c.id}</span> {c.title} <span className="opacity-70">· {c.status}{c.group_key ? ` · ${c.group_key}` : ""}</span></li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
      {shown < total && shown < 200 && (
        <button type="button" onClick={() => setLimit((l) => Math.min(200, l + PAGE))} data-testid="fleet-sessions-more"
          className="mt-1 inline-flex items-center h-8 px-3 rounded-[5px] text-sm text-page-muted hover:text-page-title border border-card-border bg-transparent cursor-pointer">
          Load {Math.min(PAGE, total - shown)} more
        </button>
      )}
    </div>
  );
}

// ── Status strip: four numbers that answer "what is the fleet doing and does
// it have room" without scrolling (variant A, decision 1c/2a: four large
// tiles, no attention colours). Every number is derived from payloads the
// page already loads; a tile scrolls to its section.
const scrollToTestId = (id) => document.querySelector(`[data-testid="${id}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" });
// The vendor of a model the catalog does not carry (the dispatcher's slug rule).
const vendorOfSlug = (id) => (/^gpt-/.test(id) ? "codex" : /^glm-/.test(id) ? "glm" : "claude_code");
const shortVendor = (v) => (vendorMeta(v).label || v).split(" ")[0];
const cellsOf = (r) => (r && r.models && typeof r.models === "object" && !Array.isArray(r.models) ? r.models : {});

// ── Models & vendors as a MATRIX (variant C) — see the header comment ───────
function RouteMatrix({ routes, catalog, status, keys, saving, onChange, onCell, onSwitch, labelOf, vendorOf, resolveAlias, cliGap }) {
  const alive = status.filter(isAlive).sort((a, b) => new Date(b.seen_at) - new Date(a.seen_at));
  const hb = alive[0]?.routes || {}; // the freshest pod's report
  const hasReport = Object.keys(hb).some((k) => k.endsWith("_relay"));
  const fleet = routes.filter((r) => r.consumer.startsWith("fleet_"));
  if (!fleet.length) return null;
  // The pair sits together: the subagent row right after the orchestrator.
  const persona = fleet.find((x) => x.consumer === "fleet_persona");
  const rows = [];
  for (const r of fleet) {
    if (r.consumer === "fleet_persona") continue;
    rows.push(r);
    if (r.consumer === "fleet_orchestrator" && persona) rows.push(persona);
  }
  if (persona && !rows.includes(persona)) rows.push(persona);
  const vendorOfModel = (id) => vendorOf(id) || vendorOfSlug(String(id || ""));
  // Live key entries (freshest pod first) by key label.
  const live = new Map();
  for (const p of alive) for (const e of p.limits || []) {
    const label = e.key || keyNameOf(e.account);
    if (label && !live.has(label)) live.set(label, { ...e, label });
  }
  const keyVendor = (label) => live.get(label)?.vendor || keys.keys.find((k) => k.label === label)?.vendor || null;
  const columns = [...new Set([
    ...POOLED_VENDORS.filter((v) => !keys.loaded || keys.keys.some((k) => k.vendor === v) || [...live.values()].some((e) => e.vendor === v)),
    ...rows.flatMap((r) => [vendorOfModel(r.model_id), ...Object.keys(cellsOf(r))]),
  ])].filter(Boolean);
  // Per row: the routed vendor, what runs now and where, the key, the relay stops.
  const info = rows.map((r) => {
    const s = routeStatus(r, status);
    const routed = vendorOfModel(r.model_id);
    const runsModel = s.kind === "fallback" && s.runs && s.runs !== "nothing" ? s.runs : r.model_id;
    const runs = vendorOfModel(runsModel);
    const relay = Array.isArray(hb[`${r.consumer}_relay`]) ? hb[`${r.consumer}_relay`] : null;
    return { r, s, routed, runs, runsModel, key: hb[`${r.consumer}_key`] || null, relay };
  });
  const keyOf = (v) => info.find((i) => i.key && keyVendor(i.key) === v)?.key
    || [...live.values()].filter((e) => e.vendor === v).sort((a, b) => (Number(b.headroom_jobs) || 0) - (Number(a.headroom_jobs) || 0))[0]?.label
    || keys.keys.find((k) => k.vendor === v && k.enabled !== false)?.label || null;
  // The vendor where the row's NEXT job starts: the vendor of the pool's live
  // pick (`_key`); an older pod without the report falls back to what it runs.
  const nextVendorOf = (i) => (i.key && keyVendor(i.key)) || i.runs;
  const fuller = (e) => {
    const ws = (e?.windows || []).filter((w) => typeof w.utilization === "number" && ["5h", "7d"].includes(w.key));
    return ws.length ? Math.max(...ws.map((w) => w.utilization)) : null;
  };
  // Keys of a vendor in the pool (enabled), live entries when the registry is not loaded yet.
  const poolKeysOf = (v) => (keys.loaded ? keys.keys.filter((k) => k.vendor === v && k.enabled !== false).length : 0) || [...live.values()].filter((e) => e.vendor === v).length;
  const roomOf = (v) => [...live.values()].filter((e) => e.vendor === v && e.headroom_jobs != null).reduce((a, e) => a + Number(e.headroom_jobs), 0);
  // A vendor whose every live key is at its limit: the nearest reset of a full window.
  const blockedUntil = (v) => {
    const es = [...live.values()].filter((e) => e.vendor === v);
    if (!es.length || !es.every((e) => e.status === "rejected" || (fuller(e) ?? 0) >= 1)) return null;
    const rs = es.flatMap((e) => (e.windows || []).filter((w) => (w.utilization ?? 0) >= 1 && w.resets_at).map((w) => w.resets_at));
    return rs.length ? Math.min(...rs) : 0;
  };
  // "Fable week at 98% on key 'fleet' — session on opus instead of fable" → "Fable week at 98%".
  const shortReason = (t) => String(t || "").split(" — ")[0].replace(/ on key '[^']*'/, "").trim();
  const allApplied = info.every((i) => i.s.kind === "applied");

  const renderCell = (i, v) => {
    const { r, s } = i;
    const c = r.consumer;
    const meta = CONSUMER_META[c] || { label: c, icon: Bot, hint: r.description || "" };
    const models = cellsOf(r);
    const isPersona = c === "fleet_persona";
    const isRouted = v === i.routed, isNext = v === nextVendorOf(i);
    const stop = i.relay ? i.relay.find((x) => x.vendor === v) : null;
    const tid = `fleet-cell-${c}-${v}`;
    const cellKey = isNext ? (i.key || keyOf(v)) : (stop?.key || (isRouted ? i.key : null) || keyOf(v));
    const spare = Math.max(0, poolKeysOf(v) - 1);
    const spareTxt = spare ? ` · +${spare} key${spare > 1 ? "s" : ""}` : "";
    const value = isRouted ? r.model_id : (models[v] || "");
    // A registry without cells (older backend): the routed cell keeps its
    // picker, the others show what the pod reports, read-only.
    if (!Object.keys(models).length && !isRouted) {
      return <div className="inline-flex flex-col items-center gap-0.5 text-xs text-page-muted" data-testid={tid}>{stop ? labelOf(stop.model) : "—"}{stop && <span className="text-[10.5px] font-mono">{stop.key || "?"}</span>}</div>;
    }
    if (isPersona && !value) return <span className="text-page-muted opacity-60" data-testid={tid}>—</span>;
    const gap = isRouted ? cliGap(r.model_id) : null;
    const resolves = isRouted && isCliAlias(r.model_id) ? aliasResolves(r.model_id, status) : [];
    const shape = isPersona ? (v === "claude_code" ? "cli" : undefined) : "session";
    const selCls = `h-7 px-1.5 rounded-[5px] border bg-page-bg text-page-title text-xs w-full max-w-[230px] ${isNext && value ? "border-emerald-600 ring-2 ring-emerald-100" : "border-card-border"} ${!value ? "italic text-page-muted border-dashed" : ""}`;
    // The worker will run another model than this cell shows (the Fable week
    // used up → opus on the same key): one quiet line, only while it is true.
    const subst = isNext && s.kind === "fallback" && i.runsModel && vendorOfModel(i.runsModel) === v && i.runsModel !== value ? i.runsModel : null;
    const nextHint = s.kind === "fallback" && s.reason && vendorOfModel(i.runsModel) === v
      ? s.reason
      : "The key the next job of this row takes right now — the pool's live pick: lowest tier, then lowest load, then fewest running jobs, then round-robin. Grey keys under the other models = what that vendor would use.";
    return (
      <div className="inline-flex flex-col items-center gap-0.5 min-w-[150px] max-w-full" data-testid={tid}>
        <div className="flex items-center gap-1.5 w-full justify-center">
          <ModelSelect value={value} catalog={catalog} allowed={[v]} shape={shape} exclude={meta.exclude || []}
            allowNone={!isRouted && !isPersona} noneLabel="not on this vendor"
            onChange={(id) => (isRouted ? onChange(r, id) : onCell(r, v, id))} disabled={saving === c}
            testid={isRouted ? `fleet-model-select-${c}` : `fleet-model-select-${c}-${v}`} resolveAlias={resolveAlias} className={selCls} />
          {saving === c && isRouted && <Loader2 size={12} className="animate-spin text-page-muted" />}
        </div>
        {isRouted && meta.note && <div className="text-[10.5px] text-page-muted text-center leading-tight" data-testid={`fleet-note-${c}`}>{meta.note}</div>}
        {gap && (
          <div className="text-[10.5px] text-red-700 text-center" data-testid={`fleet-cli-gap-${c}`}>
            needs Claude Code CLI ≥ {gap.need} — the live worker runs {gap.have}; rebuild the fleet image first
          </div>
        )}
        {resolves.length > 0 && (
          <div className="text-[10.5px] text-page-muted" data-testid={`fleet-resolves-${c}`}>
            <Hint align="start" text={resolves.length > 1
              ? "The live workers disagree: they run different fleet images, and each image's Claude Code CLI resolves the alias on its own. The newest heartbeat is listed first. Roll the fleet so every worker runs the same image."
              : "An alias is resolved inside the worker's Claude Code CLI, not by the API — so it is only as new as the CLI in the worker image. Rebuilding the fleet image moves it."}>
              <span>→ {resolves.map(labelOf).join(" / ")} on the live worker{resolves.length > 1 ? "s — they disagree" : ""}</span>
            </Hint>
          </div>
        )}
        {value && isNext && (
          <Hint align="start" text={nextHint}>
            <div className="text-[10.5px] font-semibold text-emerald-700 whitespace-nowrap" data-testid={`fleet-cell-next-${c}`} data-vendor={v}>
              ● next job · <span className="font-mono">{cellKey || "?"}</span>{spareTxt && <span className="font-normal opacity-80">{spareTxt}</span>}
            </div>
          </Hint>
        )}
        {value && !isNext && (
          <div className="text-[10.5px] text-page-muted whitespace-nowrap" data-testid={`fleet-cell-key-${c}-${v}`}>
            <span className="font-mono">{cellKey || (hasReport ? "?" : "—")}</span>{spareTxt}
          </div>
        )}
        {subst && (
          <div className="text-[10.5px] text-page-muted text-center" data-testid={`fleet-cell-runs-${c}`} title={s.reason}>
            runs <span className="font-medium text-page-title">{labelOf(subst).replace(/\s*\(always latest.*\)$/, "")}</span>{shortReason(s.reason) ? ` · ${shortReason(s.reason)}` : ""}
          </div>
        )}
      </div>
    );
  };

  const renderHead = (i) => {
    const { r, s } = i;
    const c = r.consumer;
    const meta = CONSUMER_META[c] || { label: c, icon: Bot, hint: r.description || "" };
    const Icon = meta.icon;
    const isPersona = c === "fleet_persona";
    const models = cellsOf(r);
    // The row head says WHAT this row is and WHERE it runs — and nothing
    // else on a good day (Kirill, 2026-09-17): the name, a three-word line,
    // the switch. The full description and "changed … by …" are on hover of
    // the name; "in use · worker confirmed" is said once in the pod header;
    // a substituted model is the green cell's second line. A chip appears
    // only for an EXCEPTION the cells cannot show: switching, not taken, no
    // worker online, not applied yet.
    const changed = r.updated_by ? `Changed ${fmt(r.updated_at)} by ${r.updated_by.split("@")[0]}.` : "Never changed.";
    const exception = ["pending", "drift", "nopod", "unwired"].includes(s.kind);
    return (
      <td className={`px-4 py-2.5 align-top ${isPersona ? "pl-8" : ""}`}>
        <div className="flex items-center gap-2 font-medium text-page-title">
          {isPersona ? <span className="text-violet-500">↳</span> : <Icon size={15} className="shrink-0 text-page-muted" />}
          <Hint align="start" text={`${isPersona ? "Board builds, queued chat delegates, audits, workflow actors — the subagent model. Same session and key as the orchestrator; follows its switch." : (meta.hint || r.description || "")} ${changed}`}>
            <span data-testid={`fleet-row-name-${c}`}>{isPersona ? "Subagents" : meta.label}</span>
          </Hint>
        </div>
        {isPersona ? (
          <div className="text-xs text-page-muted mt-0.5" data-testid={`fleet-vendor-${c}`}>follows the orchestrator</div>
        ) : (
          <div className="text-xs text-page-muted mt-0.5" data-testid={`fleet-row-short-${c}`}>{meta.short || meta.hint || r.description}</div>
        )}
        {isPersona ? null : (
          <div className="inline-flex rounded-[6px] border border-card-border overflow-hidden mt-1.5" data-testid={`fleet-vendor-${c}`} role="radiogroup" aria-label="Runs on">
            {columns.map((v) => {
              const has = !!models[v] || v === i.routed;
              const lit = v === i.routed;
              const vm = vendorMeta(v);
              return (
                <button key={v} type="button" role="radio" aria-checked={lit} disabled={!has || lit || saving === c}
                  onClick={() => onSwitch(r, v)} data-testid={`fleet-switch-${c}-${v}`}
                  title={lit ? `Runs on ${vm.label} now` : has ? `Run on ${vm.label} — ${labelOf(models[v])}` : `Not on ${vm.label}: pick a model in that cell first`}
                  aria-label={vm.label}
                  className={`px-2 py-0.5 text-[10.5px] border-r last:border-r-0 border-card-border ${lit ? `${vm.cls} font-semibold cursor-default` : has ? "bg-transparent text-page-muted hover:text-page-title cursor-pointer" : "bg-transparent text-page-muted opacity-40 line-through cursor-default"}`}>
                  {shortVendor(v)}
                </button>
              );
            })}
          </div>
        )}
        {exception && (
          <div className="flex items-center gap-2 mt-1 flex-wrap text-[10.5px] text-page-muted" data-testid={`fleet-row-exception-${c}`}>
            <StatusCell route={r} statusRows={status} labelOf={labelOf} />
          </div>
        )}
      </td>
    );
  };

  return (
    <>
      <div className="flex items-center gap-2 flex-wrap mt-4 mb-0.5" data-testid="fleet-pod-fleet">
        <span className="text-sm font-medium text-page-title">Agent fleet</span>
        <span className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold bg-indigo-100 text-indigo-900">cortex-agents pod</span>
        {allApplied && alive[0] && <span className="text-[11px] text-emerald-700" data-testid="fleet-pod-fleet-confirmed">● worker confirmed all rows · {ago(alive[0].seen_at)}</span>}
      </div>
      <p className="text-xs text-page-muted mb-2">{columns.length} vendor{columns.length === 1 ? "" : "s"} with keys</p>
    <div className="border border-card-border rounded-[8px] overflow-x-auto bg-card-bg" data-testid="fleet-matrix">
      <table className="w-full text-sm border-collapse min-w-[720px]">
        <thead>
          <tr className="bg-table-header text-page-muted">
            <th className="px-4 py-2.5 font-medium text-left" style={{ width: "28%" }}>Where it runs · what · runs on</th>
            {columns.map((v) => {
              const vm = vendorMeta(v), k = keyOf(v), e = k ? live.get(k) : null, u = fuller(e), nk = poolKeysOf(v), room = roomOf(v), until = blockedUntil(v);
              const pct = u === null ? null : Math.round(u * 100);
              const color = pct === null ? "#94a3b8" : pct >= 90 ? "#dc2626" : pct >= 70 ? "#d97706" : "#16a34a";
              return (
                <th key={v} className="px-3 py-2 font-medium text-center" data-testid={`fleet-matrix-vendor-${v}`}>
                  <div className="inline-flex flex-col items-center gap-0.5">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${vm.cls}`}>{vm.label}</span>
                    <span className="text-[10.5px] text-page-muted font-normal inline-flex items-center gap-1.5 flex-wrap justify-center">
                      {!k && <span>no key in the pool</span>}
                      {nk > 1 && <span>{nk} keys ·</span>}
                      {pct !== null && <span className="w-12 h-1.5 rounded-full bg-slate-200 overflow-hidden inline-block"><span className="block h-full rounded-full" style={{ width: `${Math.min(100, pct)}%`, background: color }} /></span>}
                      {pct !== null && <span>{pct} %</span>}
                      {until !== null
                        ? <span className="text-red-700 font-medium" data-testid={`fleet-matrix-blocked-${v}`}>· limit reached{until ? ` → ${fmtClockAt(until)}` : ""}</span>
                        : e?.headroom_jobs != null && <span title="Room in jobs across the vendor's keys: (1 − load) / share per job − running jobs">· room {room.toFixed(1)} jobs</span>}
                    </span>
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          <tr className="border-t border-card-border bg-page-bg" data-testid="fleet-section-fleet">
            <td colSpan={columns.length + 1} className="px-4 py-1.5">
              <span className="text-[11px] uppercase tracking-wide font-medium text-page-title">Agent fleet (remote workers)</span>
              <span className="text-[11px] text-page-muted ml-2">— applies to the next job within a minute; a running job keeps the model it started with · each cell = the model this row runs on that vendor, the key under it = the key that vendor would use · the switch under the name = where it runs now · green = where the next job starts</span>
              {allApplied && alive[0] && <span className="text-[11px] text-emerald-700 ml-3" data-testid="fleet-matrix-confirmed">● worker confirmed all {info.length} rows · {ago(alive[0].seen_at)}</span>}
              {!hasReport && alive.length > 0 && <span className="text-[11px] text-amber-700 ml-3" data-testid="fleet-matrix-old-pod">the worker's image predates the per-lane report — relay cells show no key or order yet</span>}
            </td>
          </tr>
          {info.map((i) => {
            const pair = i.r.consumer === "fleet_orchestrator" || i.r.consumer === "fleet_persona";
            return (
              <tr key={i.r.consumer} className={`border-t border-card-border ${pair ? "border-l-[3px] border-l-violet-500" : ""}`} data-testid={`fleet-route-${i.r.consumer}`}>
                {renderHead(i)}
                {columns.map((v) => <td key={v} className="px-3 py-2.5 align-top text-center">{renderCell(i, v)}</td>)}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    </>
  );
}

function StatusStrip({ heavy, status, routes, keys }) {
  const alive = (status || []).filter(isAlive);
  const heavyRunning = heavy?.slots?.busy ?? (heavy?.running || []).length;
  const reqRunning = (heavy?.jobs || []).filter((j) => j.status === "running").length;
  const reqQueued = (heavy?.jobs || []).filter((j) => j.status !== "running").length;
  const queued = (heavy?.queued || []).length;
  const todo = heavy?.todo_count || 0;
  const pods = heavy?.slots?.alive_pods ?? alive.length;
  const freeSlots = Math.max(0, (heavy?.slots?.max || 0) - (heavy?.slots?.busy || 0));
  // Per key, the freshest live 5-hour window; disabled registry keys excluded.
  const disabled = new Set((keys?.keys || []).filter((k) => k.enabled === false).map((k) => k.label));
  // Dead tokens (registry mark, fleet/alerts.js): no headroom to report, and
  // with every enabled key dead the fleet is not claiming at all.
  const enabledKeys = (keys?.keys || []).filter((k) => k.enabled !== false);
  const invalidKeys = enabledKeys.filter((k) => k.invalid_since).map((k) => k.label);
  const allDead = enabledKeys.length > 0 && invalidKeys.length === enabledKeys.length;
  const perKey = new Map();
  for (const row of alive) {
    const seen = new Date(row.seen_at).getTime() || 0;
    for (const e of (row.limits || [])) {
      const label = e.key || keyNameOf(e.account);
      if (disabled.has(label) || invalidKeys.includes(label)) continue;
      const w = (e.windows || []).find((x) => x.key === "5h" || x.key === "five_hour");
      const prev = perKey.get(label);
      if (!prev || prev.seen < seen) perKey.set(label, { seen, u: typeof w?.utilization === "number" ? w.utilization : null, reset: w?.resets_at || null, status: e.status });
    }
  }
  let tightest = null;
  for (const [label, k] of perKey) { if (k.u == null) continue; if (!tightest || k.u > tightest.u) tightest = { label, ...k }; }
  const headroom = tightest ? Math.max(0, Math.round((1 - tightest.u) * 100)) : null;
  const resets = [...perKey.values()].map((k) => k.reset).filter(Boolean);
  const nextReset = resets.length ? Math.min(...resets) : null;
  const kinds = (routes || []).map((r) => routeStatus(r, status || []).kind);
  const routing = kinds.includes("drift") ? "drift" : kinds.includes("pending") ? "pending" : kinds.includes("nopod") ? "no worker online" : "no drift";
  const Tile = ({ k, v, s, hint, to, testid }) => (
    <button type="button" onClick={() => scrollToTestId(to)} data-testid={testid}
      className="text-left border border-card-border rounded-[8px] bg-card-bg px-3.5 py-2.5 hover:border-slate-400 cursor-pointer">
      <div className="text-[10px] uppercase tracking-wide text-page-muted"><Hint text={hint} align="start">{k}</Hint></div>
      <div className="text-xl font-semibold text-page-title tabular-nums leading-tight">{v}</div>
      {s && <div className="text-[11px] text-page-muted mt-0.5 leading-tight">{s}</div>}
    </button>
  );
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-6" data-testid="fleet-strip">
      <Tile k="Running now" v={heavyRunning + reqRunning} to="fleet-heavy" testid="fleet-strip-running"
        s={`${reqRunning} requirements · ${heavyRunning} heavy`}
        hint={`Jobs running on the fleet right now, both lanes: Board requests (heavy) and Docs generations (requirements). ${pods} worker${pods === 1 ? "" : "s"} online.`} />
      <Tile k="Waiting" v={queued + reqQueued} to="fleet-heavy" testid="fleet-strip-waiting"
        s={`${todo} parked · ${freeSlots} slot${freeSlots === 1 ? "" : "s"} free`}
        hint="Requests and generations waiting for a free worker. Parked on Board = To-do cards nobody has queued yet, so workers do not see them." />
      <Tile k="Pool headroom" v={allDead ? "none" : headroom == null ? "—" : `${headroom}%`} to="fleet-keys" testid="fleet-strip-headroom"
        s={allDead ? "not claiming — no usable key" : `${tightest ? `of 5 h · ${tightest.label}${tightest.status && tightest.status !== "allowed" ? ` · ${tightest.status}` : ""}` : "no live key yet"}${invalidKeys.length ? ` · ${invalidKeys.length} key${invalidKeys.length === 1 ? "" : "s"} invalid` : ""}`}
        hint={allDead
          ? "Every enabled key's token is refused by the provider, so the fleet claims nothing: cards wait on the Board until a token is re-issued (claude setup-token) and rotated here."
          : "What is left of the 5-hour window on the key with the LEAST room. New jobs start on the key with the lowest load, so this is the floor, not the average. A key whose token is invalid is left out."} />
      <Tile k="Next reset" v={nextReset ? fmtClockAt(nextReset) : "—"} to="fleet-routes" testid="fleet-strip-reset"
        s={`${nextReset ? `in ${fmtDur(Math.max(0, nextReset - Date.now() / 1000))}` : "—"} · routing ${routing}`}
        hint="When the nearest 5-hour window resets, your local time. Routing: whether every fleet route is applied by the live workers (drift = a worker ran another model than routed; pending = a change not yet picked up)." />
    </div>
  );
}

export default function Fleet() {
  const [routes, setRoutes] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [status, setStatus] = useState([]); // fleet_status rows: worker pods' heartbeats
  const [quota, setQuota] = useState([]);
  const [chatTransport, setChatTransport] = useState("unknown"); // per-subscription windows with burn rate + forecast
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(null); // consumer (or "chat") being saved
  const [chatExpanded, setChatExpanded] = useState(false);
  const [heavy, setHeavy] = useState(null); // GET /api/fleet/heavy payload
  const [heavyError, setHeavyError] = useState(null);
  // Key registry (GET /api/fleet/keys): configured?, offered vendors, rows
  // (fingerprints only), recent events. `manage: false` when this user may
  // not manage keys (403) — the blocks then render without a menu.
  const [keys, setKeys] = useState({ configured: false, vendors: [], keys: [], events: [], manage: false, loaded: false });
  const [keyDialog, setKeyDialog] = useState(null); // { mode: "add" } | { mode: "rotate", label, vendor }
  const [noteOpen, setNoteOpen] = useState(null); // the key whose note row is open in the keys table
  const { confirm, prompt } = useConfirm();

  const loadKeys = async () => {
    try {
      const res = await getFleetKeys();
      setKeys({ configured: !!res.configured, vendors: res.vendors || [], keys: res.keys || [], events: res.events || [], manage: true, loaded: true });
    } catch (e) {
      setKeys((k) => ({ ...k, manage: e?.status !== 403 && k.manage, loaded: true }));
    }
  };
  const keyAction = async (action, reg, payload) => {
    try {
      if (action === "setnote") {
        await updateFleetKey(reg.label, { note: String(payload ?? "") });
        toast.success(`Note saved for “${reg.label}”`);
      } else if (action === "enable" || action === "disable") {
        if (action === "disable" && !(await confirm({ title: `Disable key “${reg.label}”?`, description: "New jobs stop being placed on it; a job already running on it finishes. You can enable it again any time.", confirmText: "Disable" }))) return;
        await updateFleetKey(reg.label, { enabled: action === "enable" });
        toast.success(`Key “${reg.label}” ${action}d`, { description: "The fleet applies it within a minute." });
      } else if (action === "rotate") {
        setKeyDialog({ mode: "rotate", label: reg.label, vendor: reg.vendor });
        return;
      } else if (action === "note") {
        const note = await prompt({ title: `Note for “${reg.label}”`, label: "Whose subscription, why it is here", defaultValue: reg.note || "" });
        if (note === null || note === undefined) return;
        await updateFleetKey(reg.label, { note: String(note) });
      } else if (action === "remove") {
        if (!(await confirm({ title: `Remove key “${reg.label}” from the registry?`, description: "The fleet drops it within a minute; a job already running on it finishes. Its usage history stays on this page. This does not revoke the token with the provider.", confirmText: "Remove", destructive: true }))) return;
        await removeFleetKey(reg.label);
        toast.success(`Key “${reg.label}” removed`);
      }
      await loadKeys();
    } catch (e) {
      // The last enabled key of a vendor: the backend refuses once and asks
      // for an explicit confirmation — the fleet would have nothing to run on.
      if (e?.last_key && (action === "disable" || action === "remove")) {
        if (!(await confirm({ title: "This is the fleet's last key", description: `${e.message} Jobs will wait until a key is added.`, confirmText: action === "remove" ? "Remove anyway" : "Disable anyway", destructive: true }))) return;
        try {
          if (action === "remove") await removeFleetKey(reg.label, { force: true });
          else await updateFleetKey(reg.label, { enabled: false, force: true });
          toast.success(`Key “${reg.label}” ${action === "remove" ? "removed" : "disabled"} — the fleet has no key now`);
          await loadKeys();
        } catch (e2) {
          toast.error(e2?.message || "Key action failed");
        }
        return;
      }
      toast.error(e?.message || "Key action failed");
    }
  };

  const loadHeavy = async () => {
    try {
      setHeavy(await getFleetHeavy());
      setHeavyError(null);
    } catch (e) {
      setHeavyError(e?.message || "Failed to load the heavy lane");
    }
  };

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getFleetModels();
      setRoutes(res.routes || []);
      setCatalog(res.catalog || []);
      setStatus(res.status || []);
      setQuota(res.quota || []);
      setChatTransport(res.chat_transport || "unknown");
    } catch (e) {
      setError(e?.message || "Failed to load the model registry");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); loadKeys(); }, []);
  // Live swarm: poll while the tab is visible; a hidden tab skips ticks so a
  // page left open overnight doesn't hammer the API.
  useEffect(() => {
    loadHeavy();
    const t = setInterval(() => { if (document.visibilityState === "visible") loadHeavy(); }, HEAVY_POLL_MS);
    return () => clearInterval(t);
  }, []);
  // Status "applied by pod" depends on the heartbeat; refresh the registry
  // (cheap) on the same cadence while an edit is settling.
  useEffect(() => {
    const t = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      const settling = routes.some((r) => ["pending", "drift"].includes(routeStatus(r, status).kind));
      if (settling) getFleetModels().then((res) => { setStatus(res.status || []); setQuota(res.quota || []); }).catch(() => {});
    }, 15000);
    return () => clearInterval(t);
  }, [routes, status]);
  // A registry key the fleet has not reported yet ("waiting for the fleet to
  // pick it up"): refresh the heartbeat view every 15s until it shows.
  useEffect(() => {
    const t = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      const reported = new Set(status.filter(isAlive).flatMap((r) => (r.limits || []).map((e) => e.key || keyNameOf(e.account))));
      if (keys.keys.some((k) => k.enabled !== false && !reported.has(k.label))) getFleetModels().then((res) => { setStatus(res.status || []); setQuota(res.quota || []); }).catch(() => {});
    }, 15000);
    return () => clearInterval(t);
  }, [keys, status]);

  const modelOf = (id) => catalog.find((m) => m.id === id);
  const vendorOf = (id) => modelOf(id)?.vendor || "";
  const labelOf = (id) => modelOf(id)?.label || prettyModelId(id);
  // What an alias means on the live worker(s), as a label ("Fable 5.1").
  const resolveAlias = (alias) => aliasResolves(alias, status).map(labelOf).join(" / ");
  // "Runs on" for a row: the catalog vendor — except the chat rows, which show
  // the assistant's REAL transport (bedrock on a stand without a token).
  const runsOnMeta = (consumer, vendor) => {
    if (consumer.startsWith("chat_") && vendor === "claude_code" && CHAT_TRANSPORT[chatTransport]) return CHAT_TRANSPORT[chatTransport];
    return vendorMeta(vendor);
  };
  // A pinned row the live worker's CLI cannot run yet (min_cli).
  const cliGap = (modelId) => {
    const m = modelOf(modelId);
    if (!m?.min_cli) return null;
    const old = status.filter(isAlive).filter((p) => p.cli?.version && !cliAtLeast(p.cli.version, m.min_cli));
    return old.length ? { need: m.min_cli, have: old[0].cli.version } : null;
  };

  // Switching VENDOR is the consequential move on this page: confirm it, in
  // plain words, before the PUT. Same-vendor model swaps go straight through.
  const confirmVendorChange = (consumers, fromId, toId) => {
    const from = vendorOf(fromId), to = vendorOf(toId);
    if (!from || !to || from === to) return true;
    const what = consumers.map((c) => (CONSUMER_META[c] || { label: c }).label).join(", ");
    const tv = vendorMeta(to);
    const fleet = consumers.some((c) => c.startsWith("fleet_"));
    return window.confirm(
      `Switch "${what}" from ${vendorMeta(from).label} to ${tv.label} (${tv.billing})?\n\n` +
      (fleet
        ? `The worker pod must have this vendor's adapter and login; the Status column shows "applied by pod" once it has picked the route up (~60s), or flags drift if it cannot run it.`
        : `The consumer reads the registry on its next call.`),
    );
  };

  const putRoutes = async (consumers, modelId, savingKey) => {
    setSaving(savingKey);
    setError(null);
    const prev = routes;
    // Optimistic: the select reflects the choice immediately; a failed PUT
    // restores the previous rows and surfaces the error.
    setRoutes((rs) => rs.map((r) => (consumers.includes(r.consumer) ? { ...r, model_id: modelId } : r)));
    try {
      const results = await Promise.all(consumers.map((c) => setModelRoute(c, modelId)));
      setRoutes((rs) => rs.map((r) => {
        const i = consumers.indexOf(r.consumer);
        return i >= 0 ? { ...r, ...results[i].route } : r;
      }));
    } catch (e) {
      setRoutes(prev);
      setError(e?.message || `Failed to update ${consumers.join(", ")}`);
    } finally {
      setSaving(null);
    }
  };

  const onChange = (route, modelId) => {
    if (modelId === route.model_id) return;
    if (!confirmVendorChange([route.consumer], route.model_id, modelId)) return;
    putRoutes([route.consumer], modelId, route.consumer);
  };
  // The matrix: merge every row a change touched (the subagent row follows
  // the orchestrator's vendors) into the table.
  const mergeRoutes = (res) => {
    const list = Array.isArray(res?.routes) && res.routes.length ? res.routes : (res?.route ? [res.route] : []);
    if (list.length) setRoutes((rs) => rs.map((r) => list.find((x) => x.consumer === r.consumer) || r));
  };
  // The switch under a row's name: run the row on that vendor's cell.
  const onSwitch = async (route, vendor) => {
    const toId = cellsOf(route)[vendor];
    if (!toId || vendorOf(route.model_id) === vendor) return;
    if (!confirmVendorChange([route.consumer], route.model_id, toId)) return;
    setSaving(route.consumer); setError(null);
    try { mergeRoutes(await setModelRoute(route.consumer, { vendor })); }
    catch (e) { setError(e?.message || `Failed to switch ${route.consumer}`); }
    finally { setSaving(null); }
  };
  // One cell: the model the row runs whenever it runs on that vendor ("" = not on this vendor).
  const onCell = async (route, vendor, modelId) => {
    if ((cellsOf(route)[vendor] || "") === (modelId || "")) return;
    setSaving(route.consumer); setError(null);
    try { mergeRoutes(await setModelRouteCell(route.consumer, vendor, modelId || null)); }
    catch (e) { setError(e?.message || `Failed to update ${route.consumer} on ${vendor}`); }
    finally { setSaving(null); }
  };
  const chatRoutes = routes.filter((r) => CHAT_CONSUMERS.includes(r.consumer));
  const chatAllEqual = chatRoutes.length > 0 && chatRoutes.every((r) => r.model_id === chatRoutes[0].model_id);
  const chatFolded = chatAllEqual && !chatExpanded;
  const onChatChange = (modelId) => {
    if (modelId === chatRoutes[0].model_id) return;
    if (!confirmVendorChange(CHAT_CONSUMERS, chatRoutes[0].model_id, modelId)) return;
    putRoutes(chatRoutes.map((r) => r.consumer), modelId, "chat");
  };

  const renderRow = (r) => {
    const meta = CONSUMER_META[r.consumer] || { label: r.consumer, icon: Bot, hint: r.description || "" };
    const Icon = meta.icon;
    const vendor = vendorOf(r.model_id);
    const vm = runsOnMeta(r.consumer, vendor);
    const allowed = Array.isArray(r.allowed_vendors) ? r.allowed_vendors : [];
    const incompatible = !!vendor && allowed.length > 0 && !allowed.includes(vendor);
    // Fleet rows routed at a CLI alias: what the alias resolves to on the pods.
    const resolves = r.consumer.startsWith("fleet_") && isCliAlias(r.model_id) ? aliasResolves(r.model_id, status) : [];
    const gap = r.consumer.startsWith("fleet_") ? cliGap(r.model_id) : null;
    const chatExclude = r.consumer.startsWith("chat_") ? chatExcludeFor(chatTransport) : [];
    return (
      <tr key={r.consumer} className="border-t border-card-border" data-testid={`fleet-route-${r.consumer}`}>
        <td className="px-4 py-3 align-top">
          <div className="flex items-center gap-2 font-medium text-page-title">
            <Icon size={15} className="shrink-0 text-page-muted" /> {meta.label}
          </div>
          <div className="text-xs text-page-muted mt-0.5 line-clamp-2" title={meta.hint || r.description}>{meta.hint || r.description}</div>
        </td>
        <td className="px-4 py-3 align-top">
          <div className="flex items-center gap-2">
            <ModelSelect
              value={r.model_id} catalog={catalog} allowed={allowed}
              chatOnly={r.consumer.startsWith("chat_")} shape={meta.shape} exclude={[...(meta.exclude || []), ...chatExclude]}
              onChange={(v) => onChange(r, v)} disabled={saving === r.consumer || saving === "chat"}
              testid={`fleet-model-select-${r.consumer}`} resolveAlias={resolveAlias}
            />
            {saving === r.consumer && <Loader2 size={14} className="animate-spin text-page-muted" />}
          </div>
          {meta.note && (
            <div className="text-[11px] text-page-muted mt-1" data-testid={`fleet-note-${r.consumer}`}>{meta.note}</div>
          )}
          {gap && (
            <div className="text-[11px] text-red-700 mt-1" data-testid={`fleet-cli-gap-${r.consumer}`}>
              needs Claude Code CLI ≥ {gap.need} — the live worker runs {gap.have}; rebuild the fleet image first
            </div>
          )}
          {resolves.length > 0 && (
            <div className="text-[11px] text-page-muted mt-1" data-testid={`fleet-resolves-${r.consumer}`}>
              <Hint align="start" text={resolves.length > 1
                ? "The live workers disagree: they run different fleet images, and each image's Claude Code CLI resolves the alias on its own. The newest heartbeat is listed first. Roll the fleet so every worker runs the same image."
                : "An alias is resolved inside the worker's Claude Code CLI, not by the API — so it is only as new as the CLI in the worker image. This is what the alias means on the worker that reported most recently; rebuilding the fleet image moves it."}>
                <span>→ {resolves.map(labelOf).join(" / ")} on the live worker{resolves.length > 1 ? "s — they disagree" : ""}</span>
              </Hint>
            </div>
          )}
        </td>
        <td className="px-4 py-3 align-top">
          {vendor && (
            <div className="whitespace-nowrap">
              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${vm.cls}`} data-testid={`fleet-vendor-${r.consumer}`}>{vm.label}</span>
              <div className="text-[11px] text-page-muted mt-1">{vm.billing}</div>
            </div>
          )}
          {vm.note && (
            <div className="text-[11px] text-amber-700 mt-1 whitespace-normal" data-testid={`fleet-transport-note-${r.consumer}`}>{vm.note}</div>
          )}
          {incompatible && (
            <div className="text-[11px] text-red-700 mt-1" data-testid={`fleet-incompatible-${r.consumer}`}
                 title={`This consumer runs ${allowed.join(" / ")} models; the routed one is ignored downstream.`}>
              not runnable here — pick a {allowed.map((a) => vendorMeta(a).label).join(" / ")} model
            </div>
          )}
        </td>
        <td className="px-4 py-3 align-top"><StatusCell route={r} statusRows={status} labelOf={labelOf} /></td>
        <td className="px-4 py-3 align-top text-xs text-page-muted">
          {r.updated_by ? (<><div className="whitespace-nowrap">{fmt(r.updated_at)}</div><div className="truncate" title={r.updated_by}>{r.updated_by.split("@")[0]}</div></>) : <span className="opacity-70">never changed</span>}
        </td>
      </tr>
    );
  };

  // The folded chat row: one select that writes to all three chat routes.
  const renderChatFolded = () => {
    const r = chatRoutes[0];
    const vendor = vendorOf(r.model_id);
    const vm = runsOnMeta(r.consumer, vendor);
    const allowed = Array.isArray(r.allowed_vendors) ? r.allowed_vendors : [];
    return (
      <tr key="chat" className="border-t border-card-border" data-testid="fleet-route-chat">
        <td className="px-4 py-3 align-top">
          <div className="flex items-center gap-2 font-medium text-page-title">
            <MessageSquare size={15} className="shrink-0 text-page-muted" /> Default model for chat
          </div>
          <div className="text-xs text-page-muted mt-0.5">Main, engine and analyst chats</div>
          <button type="button" onClick={() => setChatExpanded(true)} data-testid="fleet-chat-customize"
                  className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-page-muted hover:text-page-title bg-transparent border-0 p-0 cursor-pointer">
            <ChevronRight size={11} /> customize per persona
          </button>
        </td>
        <td className="px-4 py-3 align-top">
          <div className="flex items-center gap-2">
            <ModelSelect value={r.model_id} catalog={catalog} allowed={allowed} chatOnly shape="api" onChange={onChatChange}
                         exclude={chatExcludeFor(chatTransport)} disabled={saving === "chat"} testid="fleet-model-select-chat" />
            {saving === "chat" && <Loader2 size={14} className="animate-spin text-page-muted" />}
          </div>
        </td>
        <td className="px-4 py-3 align-top">
          {vendor && (<div className="whitespace-nowrap">
            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${vm.cls}`} data-testid="fleet-vendor-chat">{vm.label}</span>
            <div className="text-[11px] text-page-muted mt-1">{vm.billing}</div>
          </div>)}
          {vm.note && (
            <div className="text-[11px] text-amber-700 mt-1 whitespace-normal" data-testid="fleet-transport-note-chat">{vm.note}</div>
          )}
        </td>
        <td className="px-4 py-3 align-top"><StatusCell route={r} statusRows={status} labelOf={labelOf} /></td>
        <td className="px-4 py-3 align-top text-xs text-page-muted">
          {(() => { const last = chatRoutes.filter((x) => x.updated_by).sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))[0];
            return last ? (<><div className="whitespace-nowrap">{fmt(last.updated_at)}</div><div className="truncate" title={last.updated_by}>{last.updated_by.split("@")[0]}</div></>) : <span className="opacity-70">never changed</span>; })()}
        </td>
      </tr>
    );
  };

  const sectionRows = (section) => {
    const claimed = new Set();
    for (const s of SECTIONS) { if (s === section) break; routes.forEach((r) => { if (s.match(r.consumer)) claimed.add(r.consumer); }); }
    return routes.filter((r) => !claimed.has(r.consumer) && section.match(r.consumer));
  };

  return (
    <div className="pt-[30px] pb-24 md:pb-[30px] px-4 md:px-[60px] bg-page-bg min-h-full" data-testid="fleet-page">
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-xl font-semibold text-page-title flex items-center gap-2">
          <Bot size={20} /> Agent fleet
        </h1>
        <div className="flex items-center gap-2 text-xs text-page-muted">
          {heavy?.generated_at && <span data-testid="fleet-updated">updated {ago(heavy.generated_at)}</span>}
          <button
            type="button"
            onClick={() => { load(); loadHeavy(); loadKeys(); }}
            className="inline-flex items-center justify-center h-7 w-7 rounded-[5px] text-page-muted hover:text-page-title border border-card-border bg-transparent cursor-pointer"
            title="Reload everything now (the page refreshes the live parts every 5 seconds by itself)"
            aria-label="Reload"
          >
            <RefreshCw size={13} />
          </button>
        </div>
      </div>
      <p className="text-sm text-page-muted mb-4">
        What the fleet is doing right now, the subscription keys it runs on, which model each part of the platform uses, the worker pods, and every session it ran.
      </p>

      {error && (
        <div className="mb-4 px-3 py-2 rounded-[5px] bg-red-50 text-red-700 text-sm" data-testid="fleet-error">{error}</div>
      )}

      {!loading && <StatusStrip heavy={heavy} status={status} routes={routes} keys={keys} />}

      <HeavySwarm data={heavy} error={heavyError} onChanged={loadHeavy} />
      {keyDialog && <KeyDialog key={`${keyDialog.mode}:${keyDialog.label || ""}`} state={keyDialog} vendors={keys.vendors} onClose={() => setKeyDialog(null)} onDone={() => { setKeyDialog(null); loadKeys(); }} />}

      {!loading && (
        <div className="mb-6" data-testid="fleet-keys">
          {(() => {
            // Group quota windows by subscription KEY (vendor + account). The
            // live heartbeat entries (per pod, per key) supply the busy count
            // and the pool size; the history supplies the bars.
            const groups = new Map();
            for (const w of quota) { const k = `${w.vendor}|${w.account}`; if (!groups.has(k)) groups.set(k, []); groups.get(k).push(w); }
            // Several pods can be "alive" at once (a replaced pod stays for
            // ten minutes): the FRESHEST heartbeat describes the key (source,
            // status, fingerprint), busy counts add up across pods.
            const live = new Map();
            for (const row of status) {
              if (!isAlive(row)) continue;
              const seen = new Date(row.seen_at).getTime() || 0;
              for (const e of (Array.isArray(row.limits) ? row.limits : [])) {
                const k = `${e.vendor}|${e.account}`;
                const prev = live.get(k);
                const busy = (prev?.busy || 0) + (Number(e.busy) || 0);
                live.set(k, prev && prev._seen > seen ? { ...prev, busy } : { ...e, busy, _seen: seen });
              }
            }
            // A key the live heartbeat knows but history does not (an invalid
            // token has no windows, so nothing is ever appended) still gets a
            // block — that is exactly the key someone must act on.
            for (const k of live.keys()) if (!groups.has(k)) groups.set(k, []);
            // Registry rows: a key added on the page shows at once, before any
            // heartbeat carries it (and a disabled one stays visible, greyed).
            const regByLabel = new Map(keys.keys.map((r) => [r.label, r]));
            for (const r of keys.keys) { const k = `${r.vendor}|${accountOf(r.vendor, r.label)}`; if (!groups.has(k)) groups.set(k, []); }
            const poolSizes = new Map();
            for (const k of groups.keys()) {
              const [v, acc] = k.split("|");
              const reg = regByLabel.get(keyNameOf(acc));
              if (reg && reg.enabled === false && !live.has(k)) continue; // not in the pool
              poolSizes.set(v, (poolSizes.get(v) || 0) + 1);
            }
            // An emergency env key in the pool? Only then do source badges say anything.
            const showSource = [...live.values()].some((e) => e.source === "env");
            // Which key the pool would give the NEXT job to, per vendor —
            // the dispatcher's ranking (keypool.pick, 2026-09-08): tier
            // (invalid 3, rejected 2, close to the limit 1, usable 0), then
            // LOAD = the fuller of the 5-hour and 7-day windows (a window
            // past its reset counts as empty; the Fable week is not part of
            // it), then jobs already running, then list order.
            const nowS = Date.now() / 1000;
            const windowOf = (k, ws, name) => {
              const lw = (live.get(k)?.windows || []).find((w) => w.key === name || w.key === (name === "5h" ? "five_hour" : "seven_day"));
              if (lw && typeof lw.utilization === "number") return (lw.resets_at && lw.resets_at < nowS) ? null : lw.utilization;
              const hw = ws.find((w) => w.window_key === name);
              if (!hw || hw.expired) return null;
              return Number(hw.utilization) || 0;
            };
            const loadOf = (k, ws) => {
              const parts = [["5h", windowOf(k, ws, "5h")], ["7d", windowOf(k, ws, "7d")]].filter(([, u]) => u !== null);
              if (!parts.length) return { u: 0, w: null, parts };
              const [w, u] = parts.reduce((m, p) => (p[1] > m[1] ? p : m));
              return { u, w, parts };
            };
            const tierOf = (k, load, reg) => {
              const st = live.get(k)?.status;
              if (st === "invalid" || reg?.invalid_since) return 3;
              if (st === "rejected") return 2;
              if (st === "allowed_warning" || load.u >= 0.9) return 1;
              return 0;
            };
            // Does the fleet lease this vendor's keys? Either the page knows the
            // vendor is pooled, or a live heartbeat already carries one of its
            // keys (a newer fleet than this page). An unpooled vendor gets no
            // "next job", no "none usable", no relay line — its keys wait.
            const pooledLive = (v) => POOLED_VENDORS.includes(v) || (byVendor.get(v) || []).some((k) => live.has(k));
            const rank = (k) => (k === "5h" || k === "five_hour") ? 0 : (k === "7d" || k === "seven_day") ? 1 : 2;
            const byVendor = new Map();
            for (const k of groups.keys()) { const [v] = k.split("|"); if (!byVendor.has(v)) byVendor.set(v, []); byVendor.get(v).push(k); }
            const vendorOrder = (v) => { const i = POOLED_VENDORS.indexOf(v); return i < 0 ? 99 : i; };
            // One row per key: pooled vendors in the matrix's order, disabled and
            // invalid keys last within their vendor. `health` = the key's own word.
            const rowsOf = [...groups.keys()].map((k) => {
              const [vendor, account] = k.split("|");
              const name = keyNameOf(account);
              const reg = regByLabel.get(name) || null;
              const ws = [...(groups.get(k) || [])].sort((x, y) => rank(x.window_key) - rank(y.window_key));
              const lv = live.get(k) || null;
              const load = loadOf(k, ws);
              const disabled = reg?.enabled === false;
              const invalid = !!reg?.invalid_since || lv?.status === "invalid";
              const health = invalid ? "invalid"
                : disabled ? "disabled"
                : (reg && lv && lv.source === "env") ? "shadowed"
                : lv?.status === "rejected" ? "rejected"
                : tierOf(k, load, reg) === 1 ? "warning"
                : (reg && !lv && pooledLive(vendor)) ? "waiting"
                : (reg && !pooledLive(vendor)) ? "unpooled"
                : "usable";
              return { k, vendor, account, name, reg, ws, lv, health, disabled, invalid, order: [vendorOrder(vendor), disabled || invalid ? 1 : 0, name] };
            }).sort((x, y) => x.order[0] - y.order[0] || x.order[1] - y.order[1] || String(x.order[2]).localeCompare(String(y.order[2])));
            const nPool = [...poolSizes.values()].reduce((acc, n) => acc + n, 0);
            return (
              <>
                <h2 className="text-sm font-medium text-page-title mb-2 flex items-center flex-wrap gap-2">
                  <span>Subscription keys</span>
                  <span className="text-xs font-normal text-page-muted" data-testid="fleet-keys-count">· {byVendor.size} vendor{byVendor.size === 1 ? "" : "s"} · {rowsOf.length} key{rowsOf.length === 1 ? "" : "s"} · {nPool} in the pool</span>
                  <span className="flex-1" />
                  {keys.manage && keys.configured && (
                    <button type="button" onClick={() => setKeyDialog({ mode: "add" })} data-testid="fleet-key-add-card"
                      className="inline-flex items-center gap-1 h-7 px-2.5 rounded-[5px] border border-card-border bg-card-bg text-xs font-medium text-page-title hover:border-slate-400 cursor-pointer">
                      <Plus size={12} /> Add a key
                    </button>
                  )}
                </h2>
                <div className="border border-card-border rounded-[8px] overflow-x-auto bg-card-bg mb-2" data-testid="fleet-keys-table">
                  <table className="w-full text-sm border-collapse min-w-[860px]">
                    <thead>
                      <tr className="bg-table-header text-left text-page-muted">
                        <th className="px-4 py-2.5 font-medium" style={{ width: "22%" }}>Key</th>
                        <th className="px-3 py-2.5 font-medium">Vendor</th>
                        <th className="px-3 py-2.5 font-medium">State</th>
                        <th className="px-3 py-2.5 font-medium">5-hour window</th>
                        <th className="px-3 py-2.5 font-medium">7-day window</th>
                        <th className="px-3 py-2.5" />
                      </tr>
                    </thead>
                    <tbody>
                      {rowsOf.length === 0 && <tr><td colSpan={6} className="px-4 py-4 text-xs text-page-muted">No subscription key yet — add one.</td></tr>}
                      {rowsOf.map((r) => (
                        <KeyRow key={r.k} row={r} canManage={keys.manage} showSource={showSource} onAction={keyAction}
                          noteOpen={noteOpen === r.name} onToggleNote={() => setNoteOpen(noteOpen === r.name ? null : r.name)} />
                      ))}
                    </tbody>
                  </table>
                  <div className="px-4 py-1.5 border-t border-card-border text-[10.5px] text-page-muted">
                    State = the key's own health · “→ 20:20” = when the window resets · which key the next job takes and the backup order are the matrix's line above · a note: the row menu, or hover the name.
                  </div>
                </div>
                {keys.events.length > 0 && (
                  <details className="text-xs text-page-muted mb-2" data-testid="fleet-key-events">
                    <summary className="cursor-pointer">Key changes ({keys.events.length} recent)</summary>
                    <ul className="mt-1 ml-4 list-disc">
                      {keys.events.slice(0, 12).map((e, i) => (
                        <li key={i}>{ago(e.at)} — <span className="font-medium text-page-title">{e.actor_email || "?"}</span> {e.action} “{e.label}”{e.detail?.error ? `: ${e.detail.error}` : ""}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </>
            );
          })()}
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-page-muted text-sm py-10">
          <Loader2 size={16} className="animate-spin" /> Loading…
        </div>
      ) : (
        <div className="mb-6">
          <h2 className="text-sm font-medium text-page-title mb-2">Models &amp; vendors</h2>
          {/* Two pods, said before the tables (Kirill, 2026-09-16, docs/fleet-models-clarity-options.md §5 A):
              the chat is a Claude Code session in the ASSISTANT pod on one token — Claude only,
              no key pool, no relay; the fleet lanes below run in the cortex-agents pod on the pool. */}
          <div className="flex items-center gap-2 flex-wrap mb-0.5" data-testid="fleet-pod-chat">
            <span className="text-sm font-medium text-page-title">In-product chat</span>
            <span className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-900">assistant pod</span>
          </div>
          <p className="text-xs text-page-muted mb-2">a Claude Code session on one token · <span className="font-medium text-page-title">Claude only</span> · no key pool, no backups</p>
          <div className="border border-card-border rounded-[8px] overflow-hidden bg-card-bg" data-testid="fleet-routes">
            <table className="w-full text-sm border-collapse table-fixed">
              <colgroup>
                <col style={{ width: "28%" }} /><col style={{ width: "27%" }} /><col style={{ width: "16%" }} /><col style={{ width: "15%" }} /><col style={{ width: "14%" }} />
              </colgroup>
              <thead>
                <tr className="bg-table-header text-left text-page-muted">
                  <th className="px-4 py-2.5 font-medium">Where it runs · what</th>
                  <th className="px-4 py-2.5 font-medium">Model</th>
                  <th className="px-4 py-2.5 font-medium">Runs on</th>
                  <th className="px-4 py-2.5 font-medium">Status</th>
                  <th className="px-4 py-2.5 font-medium">Changed</th>
                </tr>
              </thead>
              <tbody>
                {SECTIONS.map((section) => {
                  if (section.key === "fleet") return null; // drawn as the matrix below
                  const rows = sectionRows(section);
                  if (!rows.length) return null;
                  return (
                    <React.Fragment key={section.key}>
                      <tr className="border-t border-card-border bg-page-bg" data-testid={`fleet-section-${section.key}`}>
                        <td colSpan={5} className="px-4 py-1.5">
                          <span className="text-[11px] uppercase tracking-wide font-medium text-page-title">{section.title}</span>
                          {section.note && <span className="text-[11px] text-page-muted ml-2">— {section.note}</span>}
                          {section.key === "chat" && chatAllEqual && chatExpanded && (
                            <button type="button" onClick={() => setChatExpanded(false)} data-testid="fleet-chat-collapse"
                                    className="ml-3 inline-flex items-center gap-1 text-[11px] text-page-muted hover:text-page-title bg-transparent border-0 p-0 cursor-pointer">
                              <ChevronDown size={11} /> one model for all
                            </button>
                          )}
                          {section.key === "chat" && !chatAllEqual && (
                            <span className="text-[11px] text-amber-700 ml-2" data-testid="fleet-chat-mixed">personas run different models</span>
                          )}
                        </td>
                      </tr>
                      {section.key === "chat" && chatFolded ? renderChatFolded() : rows.map(renderRow)}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          <RouteMatrix routes={routes} catalog={catalog} status={status} keys={keys} saving={saving} onChange={onChange} onCell={onCell} onSwitch={onSwitch}
            labelOf={labelOf} vendorOf={vendorOf} resolveAlias={resolveAlias} cliGap={cliGap} />
        </div>
      )}

      {!loading && (
        <details className="-mt-4 mb-6 text-xs text-page-muted" data-testid="fleet-catalog-fold">
          <summary className="cursor-pointer">Model catalog · {catalog.length} models{catalog.filter((m) => !m.enabled).length ? ` · ${catalog.filter((m) => !m.enabled).length} disabled` : ""}</summary>
          <div className="flex flex-wrap gap-2 mt-2" data-testid="fleet-catalog">
            {catalog.map((m) => (
              <span key={m.id} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs border border-card-border ${m.enabled ? "text-page-title" : "text-page-muted line-through"}`}>
                {m.label}
                <span className={`px-1.5 py-0 rounded-full text-[10px] font-medium ${vendorMeta(m.vendor).cls}`}>{vendorMeta(m.vendor).label}</span>
              </span>
            ))}
          </div>
        </details>
      )}

      {!loading && (
        <div className="mb-6" data-testid="fleet-workers">
          <h2 className="text-sm font-medium text-page-title mb-2 flex items-center gap-2">
            <span>Workers</span>
            <span className="text-xs font-normal text-page-muted" data-testid="fleet-workers-count">
              · {status.filter(isAlive).length} online{status.length > status.filter(isAlive).length ? ` · ${status.length - status.filter(isAlive).length} replaced in the last 24 h` : ""}
            </span>
            {(() => {
              // 8d: one CLI line for the fleet when every pod online agrees;
              // a card repeats it only when its pod differs.
              const alive = status.filter(isAlive).filter((r) => r.cli?.version);
              const sig = (r) => `${r.cli.version}|${JSON.stringify(r.cli.resolves || {})}`;
              if (alive.length < 2 || new Set(alive.map(sig)).size !== 1) return null;
              const r = alive[0];
              return (
                <span className="text-xs font-normal text-page-muted" data-testid="fleet-worker-cli-all">
                  · Claude Code CLI {r.cli.version}{r.cli.resolves && Object.entries(r.cli.resolves).map(([a, id]) => <span key={a}> · {a} → {labelOf(id)}</span>)}
                </span>
              );
            })()}
          </h2>
          {status.length === 0 ? (
            <div className="border border-card-border rounded-[8px] bg-card-bg px-4 py-3 text-sm text-page-muted" data-testid="fleet-workers-empty">
              No heartbeat yet — a worker pod reports every minute once it runs a dispatcher with heartbeat support.
            </div>
          ) : (() => {
            const podCard = (row) => {
            const alive = isAlive(row);
            return (
              <div key={row.pod} className="border border-card-border rounded-[8px] bg-card-bg px-4 py-3 mb-2 flex flex-wrap gap-6 items-start" data-testid={`fleet-worker-${row.pod}`}>
                <div className="min-w-[240px]">
                  <div className="flex items-center gap-2 font-medium text-page-title">
                    <span className="inline-block size-2 rounded-full" style={{ background: alive ? "#16a34a" : "#9ca3af" }} />
                    <span>Worker</span>
                    <Hint text="The Kubernetes pod name: deployment · configuration version · instance. The version part changes on every fleet redeploy, the instance part on every restart — so a different name means a different pod, and requests claimed by an old name are orphaned.">
                      <span className="font-mono text-xs text-page-muted">{row.pod}</span>
                    </Hint>
                  </div>
                  <div className="text-xs text-page-muted mt-0.5">
                    {alive
                      ? <>online · heartbeat {ago(row.seen_at)}</>
                      : <>offline — no heartbeat for {fmtDur((Date.now() - new Date(row.seen_at).getTime()) / 1000)} · <Hint text="The worker has not reported for over 10 minutes. It is down or restarting; requests it had claimed are re-queued when a worker starts, except those waiting on a person.">worker down or restarting</Hint></>}
                  </div>
                  {row.cli?.version && !(alive && (() => { const a = status.filter(isAlive).filter((r) => r.cli?.version); const sig = (r) => `${r.cli.version}|${JSON.stringify(r.cli.resolves || {})}`; return a.length >= 2 && new Set(a.map(sig)).size === 1; })()) && (
                    <div className="text-xs text-page-muted mt-0.5" data-testid={`fleet-worker-cli-${row.pod}`}>
                      <Hint align="start" text="The Claude Code CLI baked into this worker's image, and what its model aliases resolve to. Aliases resolve inside the CLI, not at the API — so an image that fell behind keeps running an older model under an “always latest” name until the fleet image is rebuilt.">
                        <span>Claude Code CLI {row.cli.version}</span>
                      </Hint>
                      {row.cli.resolves && Object.entries(row.cli.resolves).map(([a, id]) => (
                        <span key={a}> · {a} → {labelOf(id)}</span>
                      ))}
                    </div>
                  )}
                </div>
                {/* Per-pod limits only until the history-based quota block has data. */}
                {quota.length === 0 && (Array.isArray(row.limits) ? row.limits : []).map((entry) => (
                  <LimitWindows key={`${entry.vendor}|${entry.account}`} entry={entry} />
                ))}
              </div>
            );
            };
            const aliveRows = status.filter(isAlive);
            const offlineRows = status.filter((r) => !isAlive(r)).sort((a, b) => new Date(b.seen_at) - new Date(a.seen_at));
            return (
              <>
                {aliveRows.map(podCard)}
                {aliveRows.length === 0 && <div className="border border-card-border rounded-[8px] bg-card-bg px-4 py-3 text-sm text-page-muted" data-testid="fleet-workers-none-online">No worker online — the last heartbeat was {ago(offlineRows[0]?.seen_at)}.</div>}
                {offlineRows.length > 0 && (
                  <details className="text-xs text-page-muted mt-1" data-testid="fleet-workers-offline">
                    <summary className="cursor-pointer py-1">
                      {offlineRows.length} worker{offlineRows.length === 1 ? "" : "s"} replaced in the last 24 h (redeploys and restarts) · last seen {ago(offlineRows[0].seen_at)}
                    </summary>
                    <div className="mt-2">{offlineRows.map(podCard)}</div>
                  </details>
                )}
              </>
            );
          })()}
        </div>
      )}

      <FleetSessions />


    </div>
  );
}
